import "./index.css";
import { useState, useEffect, useRef } from "react";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";
import Papa from "papaparse";
import { ArrowLeft, Square, Menu, Plus, Trash2, ChevronLeft, Bot } from "lucide-react";
import { blogPosts, isNewPost } from "./blogPosts";

type Section = "inici" | "portfolios" | "assistent" | "blog";

// Mapatge de portfolios a fitxers CSV
const portfolioFiles: Record<string, { file: string; subtitle: string }> = {
  "Berkshire Hathaway": { file: "Berkshire.csv", subtitle: "Warren Buffett" },
  "Pershing Square": { file: "Pershing_Square.csv", subtitle: "Bill Ackman" },
  "Appaloosa LP": { file: "AppaloosaLP.csv", subtitle: "David Tepper" },
  "Bridgewater Associates": { file: "Bridgewater.csv", subtitle: "Ray Dalio" },
  "Soros Fund": { file: "Soros.csv", subtitle: "George Soros" },
  "Duquesne Family": { file: "Duquesne.csv", subtitle: "Stanley Druckenmiller" },
  "Elliott Investment": { file: "Elliot.csv", subtitle: "Paul Singer" },
  "Trump Administration": { file: "Trump.csv", subtitle: "President" },
  "Nancy Pelosi": { file: "Nancy.csv", subtitle: "Democrat" },
  "Cleo Fields": { file: "Cleo.csv", subtitle: "Democrat" },
  "Josh Gottheimer": { file: "Josh.csv", subtitle: "Democrat" },
  "Ro Khanna": { file: "Ro.csv", subtitle: "Democrat" },
  "Marjorie Taylor Greene": { file: "Marjorie.csv", subtitle: "Republican" },
  "Tommy Tuberville": { file: "Tommy.csv", subtitle: "Republican" },
  "Markwayne Mullin": { file: "Markwayne.csv", subtitle: "Republican" },
  "Michael McCaul": { file: "Michael.csv", subtitle: "Republican" },
  "Dave McCormick": { file: "Dave.csv", subtitle: "Republican" },
  "Rare Earths": { file: "RareEarths.csv", subtitle: "Materials crítics" },
  "Commodities": { file: "Commodities.csv", subtitle: "Matèries primeres" },
  "Nuclear": { file: "Nuclear.csv", subtitle: "Energia nuclear" },
  "Space Technology": { file: "Space.csv", subtitle: "Tecnologia espacial" },
  "Inflation": { file: "Inflation.csv", subtitle: "Protecció inflació" },
  "Drones": { file: "Drones.csv", subtitle: "Vehicles aeris" },
  "Batteries": { file: "Batteries.csv", subtitle: "Emmagatzematge" },
  "Renewable Energy": { file: "Renewable.csv", subtitle: "Energia verda" },
  "Biotechnology": { file: "Biotechnology.csv", subtitle: "Biotech" },
  "Stability": { file: "Stability.csv", subtitle: "Empreses estables" },
};

export const App = () => {
  const [activeSection, setActiveSection] = useState<Section>("inici");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const path = window.location.pathname.slice(1);

    console.log('[App] Initializing with path:', path);

    // Check if we're in a blog article URL
    if (path.startsWith("blog")) {
      console.log('[App] Setting activeSection to blog');
      setActiveSection("blog");
    } else if (path && ["inici", "portfolios", "assistent"].includes(path)) {
      console.log('[App] Setting activeSection to:', path);
      setActiveSection(path as Section);
    } else if (!path) {
      console.log('[App] Setting activeSection to inici (default)');
      setActiveSection("inici");
    }

    isInitialLoadRef.current = false;
  }, []);

  // Set CSS custom property for viewport height (handles mobile browser chrome)
  // Only set once on mount, not on scroll/resize to avoid jumpy behavior
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVh();
    // Only update on orientation change, not on resize (which fires on scroll in mobile)
    window.addEventListener('orientationchange', setVh);
    
    return () => {
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  const handleNavClick = (section: Section) => {
    setActiveSection(section);
    setIsMobileMenuOpen(false);
    window.history.pushState({}, "", `/${section === "inici" ? "" : section}`);
    window.scrollTo(0, 0);
  };

  const handleKeyDown = (event: React.KeyboardEvent, section: Section) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavClick(section);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.slice(1);

      console.log('[App] PopState event, path:', path);

      // Check if we're in a blog article URL
      if (path.startsWith("blog")) {
        console.log('[App] PopState: Setting activeSection to blog');
        setActiveSection("blog");
      } else if (path && ["inici", "portfolios", "assistent"].includes(path)) {
        console.log('[App] PopState: Setting activeSection to:', path);
        setActiveSection(path as Section);
      } else if (!path) {
        console.log('[App] PopState: Setting activeSection to inici');
        setActiveSection("inici");
      }

      window.scrollTo(0, 0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only trigger if not typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const keyMap: Record<string, Section> = {
        '1': 'inici',
        '2': 'portfolios',
        '3': 'assistent',
        '4': 'blog'
      };

      const section = keyMap[event.key];
      if (section) {
        handleNavClick(section);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isInitialLoadRef.current) {
      window.scrollTo(0, 0);
    }
  }, [activeSection]);

  return (
    <div className="flex min-h-screen bg-white">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-50">
        <a 
          href="/"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick("inici");
          }}
          className="flex items-center gap-2"
          aria-label="Anar a Inici"
        >
          <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-gray-900">2147</h1>
        </a>
        
        <button
          type="button"
          onClick={toggleMobileMenu}
          className="p-2 text-gray-700 hover:text-gray-900 transition-colors duration-150 ease"
          aria-label={isMobileMenuOpen ? "Tancar menú" : "Obrir menú"}
          aria-expanded={isMobileMenuOpen}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {isMobileMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40 animate-fade-in"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu */}
      <aside
        className={`lg:hidden fixed top-[73px] left-0 right-0 bottom-0 bg-white z-40 p-8 transition-transform duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className="flex flex-col gap-6" role="navigation" aria-label="Navegació principal">
          <a
            href="/"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${
              activeSection === "inici" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("inici");
            }}
            aria-label="Anar a Inici"
            aria-current={activeSection === "inici" ? "page" : undefined}
          >
            Inici
          </a>
          <a
            href="/portfolios"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-center gap-3 ${
              activeSection === "portfolios" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("portfolios");
            }}
            aria-label="Anar a Portfolios"
            aria-current={activeSection === "portfolios" ? "page" : undefined}
          >
            {activeSection === "portfolios" && (
              <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
            )}
            Portfolios
          </a>
          <a
            href="/assistent"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-center gap-3 ${
              activeSection === "assistent" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("assistent");
            }}
            aria-label="Anar a Assistent"
            aria-current={activeSection === "assistent" ? "page" : undefined}
          >
            {activeSection === "assistent" && (
              <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
            )}
            Assistent
          </a>
          <a
            href="/blog"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-center gap-3 ${
              activeSection === "blog" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("blog");
            }}
            aria-label="Anar a Blog"
            aria-current={activeSection === "blog" ? "page" : undefined}
          >
            {activeSection === "blog" && (
              <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
            )}
            Blog
          </a>
        </nav>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 p-8 flex-col gap-8 border-r border-gray-100 animate-fade-in-left fixed left-0 top-0 h-screen">
        {/* Logo/Title */}
        <a 
          href="/"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick("inici");
          }}
          className="flex items-center gap-2 relative cursor-pointer group"
          aria-label="Anar a Inici"
        >
          <div
            className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] will-change-transform ${
              activeSection === "inici" ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden="true"
          />
          <h1 className={`text-2xl lg:text-3xl font-bold text-gray-900 transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] ${
            activeSection === "inici" ? "" : "-ml-5"
          }`}>
            2147
          </h1>
        </a>

        {/* Navigation */}
        <nav className="flex flex-col gap-4 relative" role="navigation" aria-label="Navegació principal">
          <a
            href="/"
            className={`text-left text-sm lg:text-base transition-colors duration-150 ease ${
              activeSection === "inici" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("inici");
            }}
            onKeyDown={(e) => handleKeyDown(e, "inici")}
            tabIndex={0}
            aria-label="Anar a Inici"
            aria-current={activeSection === "inici" ? "page" : undefined}
          >
            Inici
          </a>
          <a
            href="/portfolios"
            className={`text-left text-sm lg:text-base transition-colors duration-150 ease flex items-center gap-2 ${
              activeSection === "portfolios" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("portfolios");
            }}
            onKeyDown={(e) => handleKeyDown(e, "portfolios")}
            tabIndex={0}
            aria-label="Anar a Portfolios"
            aria-current={activeSection === "portfolios" ? "page" : undefined}
          >
            <div
              className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] will-change-transform ${
                activeSection === "portfolios" ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              aria-hidden="true"
            />
            <span className={`transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] ${
              activeSection === "portfolios" ? "" : "-ml-5"
            }`}>
              Portfolios
            </span>
          </a>
          <a
            href="/assistent"
            className={`text-left text-sm lg:text-base transition-colors duration-150 ease flex items-center gap-2 ${
              activeSection === "assistent" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("assistent");
            }}
            onKeyDown={(e) => handleKeyDown(e, "assistent")}
            tabIndex={0}
            aria-label="Anar a Assistent"
            aria-current={activeSection === "assistent" ? "page" : undefined}
          >
            <div
              className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] will-change-transform ${
                activeSection === "assistent" ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              aria-hidden="true"
            />
            <span className={`transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] ${
              activeSection === "assistent" ? "" : "-ml-5"
            }`}>
              Assistent
            </span>
          </a>
          <a
            href="/blog"
            className={`text-left text-sm lg:text-base transition-colors duration-150 ease flex items-center gap-2 ${
              activeSection === "blog" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("blog");
            }}
            onKeyDown={(e) => handleKeyDown(e, "blog")}
            tabIndex={0}
            aria-label="Anar a Blog"
            aria-current={activeSection === "blog" ? "page" : undefined}
          >
            <div
              className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] will-change-transform ${
                activeSection === "blog" ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              aria-hidden="true"
            />
            <span className={`transition-all duration-200 ease-[cubic-bezier(0.215,0.61,0.355,1)] ${
              activeSection === "blog" ? "" : "-ml-5"
            }`}>
              Blog
            </span>
          </a>
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 max-w-full ${activeSection === "assistent" ? "overflow-hidden chat-main-container" : "lg:ml-64 pt-[73px] lg:pt-0 min-h-screen"}`}>
        {activeSection === "inici" && <HomeSection handleNavClick={handleNavClick} />}
        {activeSection === "portfolios" && <PortfoliosSection />}
        {activeSection === "assistent" && <ChatSection />}
        {activeSection === "blog" && <BlogSection />}
      </main>
    </div>
  );
};

interface Trade {
  date: string;
  ticker: string;
  company: string;
  type: string;
  shares: string;
  value: string;
  change: string;
  return: string;
}

interface Holding {
  ticker: string;
  company: string;
  price: string;
  shares: string;
  allocation: string;
  change: string;
  d7?: string;
  d30?: string;
  analysts: string;
  mcap?: string;
  pe: string;
}

const PortfolioDetail = ({ name, onBack }: { name: string; onBack: () => void }) => {
  const [activeView, setActiveView] = useState<"trades" | "holdings">("trades");
  const [trades, setTrades] = useState<Trade[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPolitician, setIsPolitician] = useState(false);
  const [isTheme, setIsTheme] = useState(false);

  const portfolioInfo = portfolioFiles[name];
  const subtitle = portfolioInfo?.subtitle || "";
  
  // Detectar si és un tema
  const themeNames = ["Rare Earths", "Commodities", "Nuclear", "Space Technology", "Inflation", "Drones", "Batteries", "Renewable Energy", "Biotechnology", "Stability"];
  const isThemePortfolio = themeNames.includes(name);

  useEffect(() => {
    const loadCSV = async () => {
      setLoading(true);
      const fileName = portfolioInfo?.file;
      
      if (!fileName) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`/data/${fileName}`);
        const csvText = await response.text();
        
        // Separar per línia que contingui ---
        const lines = csvText.split('\n');
        const separatorIndex = lines.findIndex(line => line.trim().startsWith('---'));
        
        let tradesText = '';
        let holdingsText = '';
        
        if (separatorIndex !== -1) {
          // Format amb Trades i Holdings
          // Trades: tot abans del separador
          tradesText = lines.slice(0, separatorIndex).join('\n');
          
          // Holdings: després del separador, saltant línies buides
          const holdingsLines = lines.slice(separatorIndex + 1);
          // Trobar la primera línia no buida (el header de holdings)
          const firstNonEmptyIndex = holdingsLines.findIndex(line => {
            const trimmed = line.trim();
            return trimmed && !trimmed.match(/^,+$/); // No buida i no només comes
          });
          
          if (firstNonEmptyIndex !== -1) {
            holdingsText = holdingsLines.slice(firstNonEmptyIndex).join('\n');
          }
        } else {
          // Format només amb Holdings (temes)
          // Verificar si és un CSV de només holdings mirant el header
          const firstLine = lines[0]?.toLowerCase() || '';
          if (firstLine.includes('ticker') && !firstLine.includes('type')) {
            // És un CSV només d'holdings
            holdingsText = csvText;
            setIsTheme(true);
          } else {
            // És un CSV només de trades
            tradesText = csvText;
          }
        }
        
        // Parse Trades
        if (tradesText) {
          Papa.parse(tradesText.trim(), {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const firstRow: any = results.data[0] || {};
              const hasPoliticianFormat = "Amount" in firstRow;
              setIsPolitician(hasPoliticianFormat);
              
              const tradesData = results.data.map((row: any) => ({
                date: row.Date || "",
                ticker: (row.Ticker || "").split(" ")[0],
                company: row.Company || "",
                type: row.Type || "",
                shares: row.Shares || row.Amount || "",
                value: row.Value || "",
                change: row.Change || "",
                return: row.Return || "",
              })).filter((t: Trade) => t.ticker && t.date);
              
              setTrades(tradesData);
            },
          });
        }
        
        // Parse Holdings
        if (holdingsText) {
          Papa.parse(holdingsText.trim(), {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const holdingsData = results.data.map((row: any) => {
                // Netejar valors que contenen dates o són invàlids
                const cleanValue = (val: string) => {
                  if (!val || val === "-") return "-";
                  // Si conté una data (detecta format YYYY-MM-DD o timestamps)
                  if (val.includes("-") && val.match(/\d{4}-\d{2}-\d{2}/)) return "-";
                  if (val.includes(":") && val.includes("00:00:00")) return "-";
                  return val;
                };
                
                return {
                  ticker: (row.Ticker || "").split(" ")[0],
                  company: row.Company || "",
                  price: row.Price || "",
                  shares: row.Shares || row["Dollar Amount"] || "",
                  allocation: row.Allocation || "",
                  change: row.Change || "",
                  d7: row["7D"] || "",
                  d30: row["30D"] || "",
                  analysts: cleanValue(row.Analysts || ""),
                  mcap: cleanValue(row.Mcap || ""),
                  pe: cleanValue(row["P/E"] || ""),
                };
              }).filter((h: Holding) => h.ticker && h.company);
              
              setHoldings(holdingsData);
            },
          });
        }
        
      } catch (error) {
        console.error("Error loading CSV:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCSV();
  }, [name, portfolioInfo]);

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Carregant...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full max-w-full px-4 lg:px-8 py-4 lg:py-8 animate-fade-in-up">
      {/* Fixed Header Container */}
      <div className="mb-4">
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-150 ease"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Tornar
        </button>

        {/* Header */}
        <div className="mb-4">
          <h2 className="text-2xl lg:text-3xl font-medium text-gray-900 mb-1">{name}</h2>
          {subtitle && <p className="text-sm lg:text-base text-gray-600">{subtitle}</p>}
        </div>

        {/* Tabs - Only show if not a theme */}
        {!isThemePortfolio && (
          <div className="flex gap-4 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveView("trades")}
              className={`pb-3 px-1 text-sm font-medium transition-colors duration-150 ease border-b-2 ${
                activeView === "trades"
                  ? "border-[#9DD566] text-gray-900"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              Trades ({trades.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveView("holdings")}
              className={`pb-3 px-1 text-sm font-medium transition-colors duration-150 ease border-b-2 ${
                activeView === "holdings"
                  ? "border-[#9DD566] text-gray-900"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              Holdings ({holdings.length})
            </button>
          </div>
        )}
      </div>

      {/* Table container */}
      <div className="w-full rounded-lg border border-gray-200 bg-white overflow-x-auto">
        {/* Trades Table */}
        {!isThemePortfolio && activeView === "trades" && (
          <table className="w-full text-sm" style={{ minWidth: '900px' }}>
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Data</th>
                <th className="text-left py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Ticker</th>
                <th className="text-left py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Empresa</th>
                <th className="text-left py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Tipus</th>
                <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">
                  {isPolitician ? "Import" : "Accions"}
                </th>
                {!isPolitician && <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Valor</th>}
                {!isPolitician && <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Canvi</th>}
                <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap pr-4">Return</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {trades.map((trade, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-3 px-3 lg:px-4 text-gray-600 text-sm whitespace-nowrap">{trade.date}</td>
                  <td className="py-3 px-3 lg:px-4 font-mono font-medium text-gray-900 text-sm whitespace-nowrap">{trade.ticker}</td>
                  <td className="py-3 px-3 lg:px-4 text-gray-900 text-sm max-w-xs truncate" title={trade.company}>{trade.company}</td>
                  <td className="py-3 px-3 lg:px-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      trade.type === "New" || trade.type === "Buy" ? "bg-green-100 text-green-800" :
                      trade.type === "Added" ? "bg-blue-100 text-blue-800" :
                      trade.type === "Exit" || trade.type === "Sell" || trade.type === "Sell Partial" ? "bg-red-100 text-red-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {trade.type}
                    </span>
                  </td>
                  <td className="py-3 px-3 lg:px-4 text-right text-gray-900 text-sm whitespace-nowrap">{trade.shares}</td>
                  {!isPolitician && <td className="py-3 px-3 lg:px-4 text-right text-gray-900 text-sm whitespace-nowrap">{trade.value}</td>}
                  {!isPolitician && <td className="py-3 px-3 lg:px-4 text-right text-gray-600 text-sm whitespace-nowrap">{trade.change}</td>}
                  <td className={`py-3 px-3 lg:px-4 text-right font-medium text-sm whitespace-nowrap pr-4 ${
                    trade.return.includes("-") ? "text-red-600" : "text-green-600"
                  }`}>{trade.return}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Holdings Table */}
        {(isThemePortfolio || activeView === "holdings") && (
          <table className="w-full text-sm" style={{ minWidth: isThemePortfolio ? '800px' : '1100px' }}>
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Ticker</th>
                <th className="text-left py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Empresa</th>
                <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Preu</th>
                {!isThemePortfolio && (
                  <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">
                    {isPolitician ? "Import" : "Accions"}
                  </th>
                )}
                {!isThemePortfolio && (
                  <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Al·locació</th>
                )}
                <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Canvi</th>
                {holdings[0]?.d7 && <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">7D</th>}
                {holdings[0]?.d30 && <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">30D</th>}
                {holdings[0]?.analysts && <th className="text-left py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Analistes</th>}
                {holdings[0]?.mcap && <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">Mcap</th>}
                <th className="text-right py-3 px-3 lg:px-4 font-medium text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap pr-4">P/E</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {holdings.map((holding, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-3 px-3 lg:px-4 font-mono font-medium text-gray-900 text-sm whitespace-nowrap">{holding.ticker}</td>
                  <td className="py-3 px-3 lg:px-4 text-gray-900 text-sm max-w-xs truncate" title={holding.company}>{holding.company}</td>
                  <td className="py-3 px-3 lg:px-4 text-right text-gray-900 text-sm whitespace-nowrap">{holding.price}</td>
                  {!isThemePortfolio && (
                    <td className="py-3 px-3 lg:px-4 text-right text-gray-900 text-sm whitespace-nowrap">{holding.shares}</td>
                  )}
                  {!isThemePortfolio && (
                    <td className="py-3 px-3 lg:px-4 text-right font-medium text-gray-900 text-sm whitespace-nowrap">{holding.allocation}</td>
                  )}
                  <td className={`py-3 px-3 lg:px-4 text-right font-medium text-sm whitespace-nowrap ${
                    holding.change.includes("-") ? "text-red-600" : "text-green-600"
                  }`}>{holding.change}</td>
                  {holding.d7 && <td className={`py-3 px-3 lg:px-4 text-right text-sm whitespace-nowrap ${
                    holding.d7.includes("-") ? "text-red-600" : "text-green-600"
                  }`}>{holding.d7}</td>}
                  {holding.d30 && <td className={`py-3 px-3 lg:px-4 text-right text-sm whitespace-nowrap ${
                    holding.d30.includes("-") ? "text-red-600" : "text-green-600"
                  }`}>{holding.d30}</td>}
                  {holding.analysts && (
                    <td className="py-3 px-3 lg:px-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        holding.analysts === "Buy" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                      }`}>
                        {holding.analysts}
                      </span>
                    </td>
                  )}
                  {holding.mcap && <td className="py-3 px-3 lg:px-4 text-right text-gray-600 text-sm whitespace-nowrap">{holding.mcap}</td>}
                  <td className="py-3 px-3 lg:px-4 text-right text-gray-900 text-sm whitespace-nowrap pr-4">{holding.pe}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

const HomeSection = ({ handleNavClick }: { handleNavClick: (section: Section) => void }) => {
  return (
    <section className="w-full max-w-4xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          2147 és una plataforma per consultar i entendre informació d'inversió a partir de dades públiques, explicada de manera clara i ordenada.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La idea és tenir en un sol lloc informació que normalment està dispersa, i presentar-la de forma que es pugui llegir amb calma i sentit, sense soroll ni promeses exagerades.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          A <a href="/portfolios" onClick={(e) => { e.preventDefault(); handleNavClick('portfolios'); }} className="font-medium text-gray-900 underline decoration-[#9DD566] decoration-2 underline-offset-2 hover:text-[#9DD566] transition-colors cursor-pointer">Portfolios</a> pots veure què han declarat grans inversors i fons a través dels formularis oficials. Es poden revisar les seves posicions, observar com canvien amb el temps i entendre millor en quines empreses posen l'atenció. Les dades sempre tenen retard, així que no serveixen per copiar moviments, sinó per analitzar estratègies, estils d'inversió i tendències generals.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          L'<a href="/assistent" onClick={(e) => { e.preventDefault(); handleNavClick('assistent'); }} className="font-medium text-gray-900 underline decoration-[#9DD566] decoration-2 underline-offset-2 hover:text-[#9DD566] transition-colors cursor-pointer">Assistent</a> és un espai per fer preguntes sobre empreses, conceptes financers o decisions d'inversió. L'objectiu és obtenir respostes entenedores, amb context i basades en informació real, evitant tecnicismes innecessaris. És una eina de suport per entendre millor el que estàs mirant, no un sistema de recomanacions.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Al <a href="/blog" onClick={(e) => { e.preventDefault(); handleNavClick('blog'); }} className="font-medium text-gray-900 underline decoration-[#9DD566] decoration-2 underline-offset-2 hover:text-[#9DD566] transition-colors cursor-pointer">Blog</a> es publiquen continguts sobre empreses, mercats i temes relacionats amb la inversió. A vegades són anàlisis més pausades, altres cops comentaris d'actualitat, però sempre amb enfocament explicatiu: entendre què ha passat i per què pot ser rellevant.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-8">
          2147 no diu què comprar ni quan fer-ho. És una eina per llegir, observar i entendre millor el món de la inversió, de manera tranquil·la i amb més context.
        </p>

        <div className="pt-6 border-t border-gray-200 hidden lg:block">
          <p className="text-sm text-gray-500 mb-3">Pots navegar per les pàgines utilitzant el teclat:</p>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-gray-600 font-mono text-xs">1</kbd>
              <span className="text-gray-600">Inici</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-gray-600 font-mono text-xs">2</kbd>
              <span className="text-gray-600">Portfolios</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-gray-600 font-mono text-xs">3</kbd>
              <span className="text-gray-600">Assistent</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-gray-600 font-mono text-xs">4</kbd>
              <span className="text-gray-600">Blog</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PortfoliosSection = () => {
  const [activeTab, setActiveTab] = useState<"funds" | "politicians" | "themes">("funds");
  const [selectedPortfolio, setSelectedPortfolio] = useState<string | null>(null);

  const funds = [
    { name: "Berkshire Hathaway", subtitle: "Warren Buffett" },
    { name: "Pershing Square", subtitle: "Bill Ackman" },
    { name: "Appaloosa LP", subtitle: "David Tepper" },
    { name: "Bridgewater Associates", subtitle: "Ray Dalio" },
    { name: "Soros Fund", subtitle: "George Soros" },
    { name: "Duquesne Family", subtitle: "Stanley Druckenmiller" },
    { name: "Elliott Investment", subtitle: "Paul Singer" },
  ];

  const politicians = [
    { name: "Trump Administration", subtitle: "President" },
    { name: "Nancy Pelosi", subtitle: "Democrat" },
    { name: "Cleo Fields", subtitle: "Democrat" },
    { name: "Josh Gottheimer", subtitle: "Democrat" },
    { name: "Ro Khanna", subtitle: "Democrat" },
    { name: "Marjorie Taylor Greene", subtitle: "Republican" },
    { name: "Tommy Tuberville", subtitle: "Republican" },
    { name: "Markwayne Mullin", subtitle: "Republican" },
    { name: "Michael McCaul", subtitle: "Republican" },
    { name: "Dave McCormick", subtitle: "Republican" },
  ];

  const themes = [
    { name: "Rare Earths", subtitle: "Materials crítics" },
    { name: "Commodities", subtitle: "Matèries primeres" },
    { name: "Nuclear", subtitle: "Energia nuclear" },
    { name: "Space Technology", subtitle: "Tecnologia espacial" },
    { name: "Inflation", subtitle: "Protecció inflació" },
    { name: "Drones", subtitle: "Vehicles aeris" },
    { name: "Batteries", subtitle: "Emmagatzematge" },
    { name: "Renewable Energy", subtitle: "Energia verda" },
    { name: "Biotechnology", subtitle: "Biotech" },
    { name: "Stability", subtitle: "Empreses estables" },
  ];

  const getCurrentList = () => {
    switch (activeTab) {
      case "funds":
        return funds;
      case "politicians":
        return politicians;
      case "themes":
        return themes;
    }
  };

  const handlePortfolioClick = (name: string) => {
    setSelectedPortfolio(name);
  };

  const handleBackClick = () => {
    setSelectedPortfolio(null);
  };

  if (selectedPortfolio && portfolioFiles[selectedPortfolio]) {
    return <PortfolioDetail name={selectedPortfolio} onBack={handleBackClick} />;
  }

  return (
    <section className="w-full max-w-6xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      <div className="prose prose-lg max-w-none mb-8">
        <h2 className="text-3xl font-medium text-gray-900 mb-4">Portfolios</h2>
        
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Cada trimestre, els grans fons d'inversió han de publicar un formulari 13F a la SEC. 
          Aquí pots veure què tenen, què han comprat i què han venut. També pots seguir els 
          portfolios de polítics americans i explorar portfolios temàtics.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-8 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab("funds")}
          className={`pb-3 px-1 text-sm font-medium transition-colors duration-150 ease border-b-2 ${
            activeTab === "funds"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Fons d'Inversió
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("politicians")}
          className={`pb-3 px-1 text-sm font-medium transition-colors duration-150 ease border-b-2 ${
            activeTab === "politicians"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Polítics
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("themes")}
          className={`pb-3 px-1 text-sm font-medium transition-colors duration-150 ease border-b-2 ${
            activeTab === "themes"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Temes
        </button>
      </div>

      {/* Grid de portfolios */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
        {getCurrentList().map((item, index) => (
          <div
            key={index}
            onClick={() => handlePortfolioClick(item.name)}
            className="bg-white border border-gray-200 rounded-lg p-5 hover:border-gray-300 hover:shadow-md transition-all duration-150 ease cursor-pointer group"
          >
            <h3 className="font-medium text-gray-900 mb-1 group-hover:text-[#9DD566] transition-colors duration-150 ease">
              {item.name}
            </h3>
            <p className="text-sm text-gray-600">{item.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="bg-gray-50 p-6 rounded-lg border-2 border-[#9DD566]">
        <p className="text-sm text-gray-700 leading-relaxed">
          <span className="font-medium">Nota:</span> Els 13F surten amb 45 dies de retard. 
          No es tracta de copiar, sinó d'entendre on posen el focus els grans inversors 
          i fer la teva pròpia recerca.
        </p>
      </div>
    </section>
  );
};

// Component per renderitzar markdown simple
const MarkdownRenderer = ({ content }: { content: string }) => {
  const renderContent = (text: string) => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let listItems: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let tableRows: string[][] = [];
    let tableHeader: string[] | null = null;
    let inTable = false;
    
    const flushList = () => {
      if (listItems.length > 0 && listType) {
        const ListTag = listType;
        elements.push(
          <ListTag key={elements.length} className={listType === 'ul' ? 'list-disc list-inside space-y-1 my-2' : 'list-decimal list-inside space-y-1 my-2'}>
            {listItems.map((item, i) => (
              <li key={i}>{renderInline(item)}</li>
            ))}
          </ListTag>
        );
        listItems = [];
        listType = null;
      }
    };

    const flushTable = () => {
      if (tableHeader && tableRows.length > 0) {
        elements.push(
          <div key={elements.length} className="my-3 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {tableHeader.map((cell, i) => (
                    <th key={i} className="px-3 py-2 text-left font-semibold text-gray-900 border-b border-gray-200">
                      {renderInline(cell.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="px-3 py-2 text-gray-700 border-b border-gray-100">
                        {renderInline(cell.trim())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      tableHeader = null;
      tableRows = [];
      inTable = false;
    };

    const renderInline = (line: string): React.ReactNode => {
      const parts: React.ReactNode[] = [];
      let remaining = line;
      let key = 0;
      
      while (remaining) {
        // Match bold
        const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
        if (boldMatch) {
          const index = boldMatch.index!;
          if (index > 0) {
            parts.push(<span key={key++}>{remaining.slice(0, index)}</span>);
          }
          parts.push(<strong key={key++} className="font-semibold">{boldMatch[1] || boldMatch[2]}</strong>);
          remaining = remaining.slice(index + boldMatch[0].length);
        } else {
          // Match italic
          const italicMatch = remaining.match(/\*(.+?)\*|_(.+?)_/);
          if (italicMatch) {
            const index = italicMatch.index!;
            if (index > 0) {
              parts.push(<span key={key++}>{remaining.slice(0, index)}</span>);
            }
            parts.push(<em key={key++}>{italicMatch[1] || italicMatch[2]}</em>);
            remaining = remaining.slice(index + italicMatch[0].length);
          } else {
            // Match inline code
            const codeMatch = remaining.match(/`(.+?)`/);
            if (codeMatch) {
              const index = codeMatch.index!;
              if (index > 0) {
                parts.push(<span key={key++}>{remaining.slice(0, index)}</span>);
              }
              parts.push(<code key={key++} className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">{codeMatch[1]}</code>);
              remaining = remaining.slice(index + codeMatch[0].length);
            } else {
              parts.push(<span key={key++}>{remaining}</span>);
              break;
            }
          }
        }
      }
      
      return parts.length === 1 ? parts[0] : <>{parts}</>;
    };

    const isTableRow = (line: string) => {
      const trimmed = line.trim();
      return trimmed.startsWith('|') && trimmed.endsWith('|');
    };

    const isTableSeparator = (line: string) => {
      const trimmed = line.trim();
      return trimmed.startsWith('|') && /^[\s|:-]+$/.test(trimmed);
    };

    const parseTableRow = (line: string): string[] => {
      return line.trim().slice(1, -1).split('|').map(cell => cell.trim());
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Table handling
      if (isTableRow(trimmed)) {
        flushList();
        
        if (isTableSeparator(trimmed)) {
          // This is the separator row, skip it but mark we're in a table
          inTable = true;
          return;
        }
        
        const cells = parseTableRow(trimmed);
        
        if (!tableHeader) {
          // First row is header
          tableHeader = cells;
        } else {
          // Data row
          tableRows.push(cells);
        }
        inTable = true;
        return;
      }
      
      // If we were in a table and hit a non-table line, flush the table
      if (inTable && !isTableRow(trimmed)) {
        flushTable();
      }
      
      // Headers
      if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(<h4 key={index} className="font-semibold text-gray-900 mt-4 mb-2">{renderInline(trimmed.slice(4))}</h4>);
      } else if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(<h3 key={index} className="font-semibold text-gray-900 text-lg mt-4 mb-2">{renderInline(trimmed.slice(3))}</h3>);
      } else if (trimmed.startsWith('# ')) {
        flushList();
        elements.push(<h2 key={index} className="font-bold text-gray-900 text-xl mt-4 mb-2">{renderInline(trimmed.slice(2))}</h2>);
      }
      // Unordered list
      else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        listItems.push(trimmed.slice(2));
      }
      // Ordered list
      else if (/^\d+\.\s/.test(trimmed)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        listItems.push(trimmed.replace(/^\d+\.\s/, ''));
      }
      // Empty line
      else if (trimmed === '') {
        flushList();
        elements.push(<div key={index} className="h-2" />);
      }
      // Regular paragraph
      else {
        flushList();
        elements.push(<p key={index} className="mb-2 leading-relaxed">{renderInline(trimmed)}</p>);
      }
    });
    
    flushList();
    flushTable();
    return elements;
  };

  return <div className="text-sm">{renderContent(content)}</div>;
};

// Component de loading amb 3 punts verds animats
const LoadingDots = () => (
  <div className="flex items-center gap-1.5 p-4">
    <div 
      className="w-2.5 h-2.5 rounded-full bg-[#9DD566] animate-bounce"
      style={{ animationDelay: '0ms', animationDuration: '600ms' }}
    />
    <div 
      className="w-2.5 h-2.5 rounded-full bg-[#9DD566] animate-bounce"
      style={{ animationDelay: '150ms', animationDuration: '600ms' }}
    />
    <div 
      className="w-2.5 h-2.5 rounded-full bg-[#9DD566] animate-bounce"
      style={{ animationDelay: '300ms', animationDuration: '600ms' }}
    />
  </div>
);

type Conversation = {
  id: string;
  title: string;
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  timestamp: number;
};

const ChatSection = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasScrolledRef = useRef(false);

  const prompts = [
    "Què és el ROIC i per què importa més que el ROE?",
    "Quines empreses tenen un moat realment defensable?",
    "Com valuaries una empresa tecnològica sense beneficis?",
    "Explica'm la diferència entre value i growth investing",
  ];

  // Load conversations from localStorage on mount and restore last conversation
  useEffect(() => {
    const savedConversations = localStorage.getItem("chat_conversations");
    if (savedConversations) {
      try {
        const parsed = JSON.parse(savedConversations);
        setConversations(parsed);
        
        // Load the most recent conversation
        if (parsed.length > 0) {
          const mostRecent = [...parsed].sort((a, b) => b.timestamp - a.timestamp)[0];
          setCurrentConversationId(mostRecent.id);
          setMessages(mostRecent.messages);
          hasScrolledRef.current = true;
        }
      } catch (error) {
        console.error("Error loading conversations:", error);
      }
    }
  }, []);

  // Save conversations to localStorage whenever they change
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem("chat_conversations", JSON.stringify(conversations));
    }
  }, [conversations]);

  // Save current conversation whenever messages change
  useEffect(() => {
    if (currentConversationId && messages.length > 0) {
      setConversations(prev => {
        const existing = prev.find(c => c.id === currentConversationId);
        if (existing) {
          return prev.map(c => 
            c.id === currentConversationId 
              ? { 
                  ...c, 
                  messages, 
                  title: messages[0]?.content.slice(0, 50) || "Nova conversa",
                  timestamp: Date.now() 
                }
              : c
          );
        } else {
          return [...prev, {
            id: currentConversationId,
            title: messages[0]?.content.slice(0, 50) || "Nova conversa",
            messages,
            timestamp: Date.now()
          }];
        }
      });
    }
  }, [messages, currentConversationId]);

  // Auto-scroll només quan hi ha missatges i s'ha interactuat amb l'assistent
  useEffect(() => {
    if ((messages.length > 0 || isLoading) && hasScrolledRef.current) {
      messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
    }
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || isStreaming) return;

    // Create new conversation if doesn't exist
    if (!currentConversationId) {
      const newId = Date.now().toString();
      setCurrentConversationId(newId);
    }

    const userMessage = inputValue.trim();
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setInputValue("");
    setIsLoading(true);
    hasScrolledRef.current = true;

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: [...messages, { role: "user", content: userMessage }],
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: `Error: ${data.error || "No s'ha pogut obtenir resposta"}` 
        }]);
        setIsLoading(false);
        return;
      }

      // Check if it's a streaming response
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('text/event-stream')) {
        // Handle streaming response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let assistantContent = "";

        // Add empty assistant message that we'll update
        setMessages(prev => [...prev, { role: "assistant", content: "" }]);
        setIsLoading(false); // Hide loading dots, show streaming text
        setIsStreaming(true);

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') break;
                try {
                  const parsed = JSON.parse(data);
                  const content = parsed.content || '';
                  if (content) {
                    assistantContent += content;
                    // Update the last message with accumulated content
                    setMessages(prev => {
                      const newMessages = [...prev];
                      newMessages[newMessages.length - 1] = {
                        role: "assistant",
                        content: assistantContent
                      };
                      return newMessages;
                    });
                  }
                } catch (e) {
                  // Skip invalid JSON
                }
              }
            }
          }
        }
        setIsStreaming(false);
      } else {
        // Handle non-streaming response (fallback)
        const data = await response.json();
        setMessages(prev => [...prev, {
          role: "assistant",
          content: data.message
        }]);
        setIsLoading(false);
      }
    } catch (error: any) {
      // Check if error was due to abort
      if (error.name === 'AbortError') {
        setIsStreaming(false);
        setIsLoading(false);
        return;
      }
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Ho sento, hi ha hagut un error de connexió. Torna-ho a intentar."
      }]);
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
      setIsLoading(false);
    }
  };

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewConversation = () => {
    const newId = Date.now().toString();
    setCurrentConversationId(newId);
    setMessages([]);
    setInputValue("");
    hasScrolledRef.current = false;
    setIsSidebarOpen(false);
  };

  const handleLoadConversation = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setCurrentConversationId(conversationId);
      setMessages(conversation.messages);
      hasScrolledRef.current = true;
      setIsSidebarOpen(false);
    }
  };

  const handleDeleteConversation = (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (currentConversationId === conversationId) {
      handleNewConversation();
    }
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Avui";
    if (days === 1) return "Ahir";
    if (days < 7) return `Fa ${days} dies`;
    return date.toLocaleDateString('ca-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <section className="w-full h-full flex flex-col animate-fade-in-up overflow-hidden">
      <div className="w-full max-w-5xl mx-auto px-2 sm:px-4 lg:px-8 py-2 sm:py-3 lg:pt-8 lg:pb-6 flex flex-col h-full overflow-hidden">
        <div className="mb-2 sm:mb-4 lg:mb-6 flex-shrink-0">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 mb-1 sm:mb-2">Assistent</h2>
          <p className="text-xs sm:text-sm lg:text-base text-gray-600 leading-relaxed">
            Pregunta sobre inversions, empreses i decisions financeres. Sense tecnicismes innecessaris.
          </p>
        </div>

        <div className="flex-1 bg-white rounded-2xl shadow-lg border border-gray-200 flex overflow-hidden relative will-change-transform">
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div 
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
              style={{
                animation: 'fadeIn 200ms cubic-bezier(0.23, 1, 0.32, 1) backwards'
              }}
              onClick={handleToggleSidebar}
              aria-hidden="true"
            />
          )}

          {/* Sidebar */}
          <aside 
            className={`bg-gradient-to-b from-gray-50 to-white border-r border-gray-200 flex flex-col will-change-transform z-50 flex-shrink-0 ${
              isSidebarOpen
                ? "w-72 lg:w-72 fixed lg:relative inset-y-0 left-0 shadow-2xl lg:shadow-none"
                : "w-10 sm:w-14 lg:w-16"
            }`}
            style={{
              transition: 'width 250ms cubic-bezier(0.23, 1, 0.32, 1), transform 250ms cubic-bezier(0.23, 1, 0.32, 1)',
              transform: isSidebarOpen ? 'translateX(0)' : 'translateX(0)'
            }}
          >
            {/* Toggle Button Area */}
            <div className={`w-full p-2 border-b border-gray-200 flex-shrink-0 flex items-center ${
              isSidebarOpen ? "justify-end" : "justify-center"
            }`}>
              <button
                type="button"
                onClick={handleToggleSidebar}
                className="group p-2 rounded-lg hover:bg-gray-200 active:scale-95 flex items-center justify-center flex-shrink-0 will-change-transform transition-all relative"
                aria-label={isSidebarOpen ? "Tancar barra lateral" : "Obrir barra lateral"}
              >
                {isSidebarOpen ? (
                  <ChevronLeft 
                    className="w-5 h-5 text-gray-300 group-hover:text-gray-700 flex-shrink-0 transition-colors"
                    strokeWidth={2}
                  />
                ) : (
                  <Menu 
                    className="w-5 h-5 text-gray-300 group-hover:text-gray-700 flex-shrink-0 transition-colors"
                    strokeWidth={2}
                  />
                )}
                {/* Visual indicator for tap area on mobile */}
                <div className="absolute inset-0 -inset-x-2 -inset-y-2 rounded-lg lg:hidden" />
              </button>
            </div>

            {/* Collapsed State Hint - Only on Desktop */}
            {!isSidebarOpen && (
              <div className="hidden lg:flex flex-1 items-center justify-center px-2 py-4">
                <div className="transform -rotate-90 whitespace-nowrap text-xs font-medium text-gray-400 tracking-wider uppercase">
                  Converses
                </div>
              </div>
            )}

            {/* Sidebar Content */}
            <div 
              className={`flex-1 overflow-hidden flex flex-col ${
                isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              style={{
                transition: 'opacity 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
            >
              {/* New Conversation Button */}
              <div className="p-3 pt-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleNewConversation}
                  className="w-full flex items-center justify-center gap-2.5 px-4 py-3 text-sm bg-gradient-to-r from-[#9DD566] to-[#8bc455] text-white rounded-xl hover:shadow-md active:scale-[0.97] font-semibold will-change-transform"
                  style={{
                    transition: 'all 150ms ease, transform 100ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  }}
                >
                  <Plus className="w-4 h-4" strokeWidth={2.5} />
                  Nova Conversa
                </button>
              </div>

              {/* Conversations List Header */}
              {conversations.length > 0 && (
                <div className="px-5 pt-3 pb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Converses
                  </p>
                </div>
              )}

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto px-3 pb-3">
                {conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 px-4">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <Bot className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 text-center leading-relaxed">
                      Les teves converses apareixeran aquí
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {[...conversations].sort((a, b) => b.timestamp - a.timestamp).map((conversation, index) => (
                      <div
                        key={conversation.id}
                        className={`group relative px-3 py-3 rounded-xl cursor-pointer will-change-transform ${
                          currentConversationId === conversation.id
                            ? "bg-[#9DD566]/10 border border-[#9DD566]/20"
                            : "hover:bg-gray-100 border border-transparent"
                        }`}
                        style={{
                          animation: `slideInLeft 200ms cubic-bezier(0.23, 1, 0.32, 1) ${index * 30}ms backwards`,
                          transition: 'all 150ms ease, transform 100ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                        }}
                        onClick={() => handleLoadConversation(conversation.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleLoadConversation(conversation.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Carregar conversa: ${conversation.title}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p 
                              className={`text-sm font-medium truncate leading-relaxed ${
                                currentConversationId === conversation.id
                                  ? "text-gray-900"
                                  : "text-gray-800"
                              }`}
                              style={{ transition: 'color 150ms ease' }}
                            >
                              {conversation.title}
                            </p>
                            <p 
                              className="text-xs mt-0.5 text-gray-500"
                              style={{ transition: 'color 150ms ease' }}
                            >
                              {formatDate(conversation.timestamp)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => handleDeleteConversation(conversation.id, e)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-100 active:scale-[0.9] flex-shrink-0 will-change-transform"
                            style={{
                              transition: 'opacity 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94), background-color 150ms ease, transform 100ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                            }}
                            aria-label="Esborrar conversa"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Main Chat Content */}
          <div className="flex-1 p-2 sm:p-4 lg:p-8 flex flex-col overflow-hidden bg-gradient-to-b from-gray-50 to-white">
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto overscroll-contain mb-2 sm:mb-4 lg:mb-6">
            {messages.length === 0 && !isLoading ? (
              <div className="flex flex-col items-center justify-center h-full px-4">
                <div 
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#9DD566] to-[#8bc455] flex items-center justify-center mb-6 shadow-lg"
                  style={{
                    animation: 'fadeIn 300ms cubic-bezier(0.23, 1, 0.32, 1) backwards'
                  }}
                >
                  <Bot className="w-8 h-8 text-white" />
                </div>
                <h3 
                  className="text-lg font-semibold text-gray-900 mb-2"
                  style={{
                    animation: 'fadeIn 300ms cubic-bezier(0.23, 1, 0.32, 1) 50ms backwards'
                  }}
                >
                  Com puc ajudar-te?
                </h3>
                <p 
                  className="text-sm text-gray-600 mb-6 text-center max-w-md"
                  style={{
                    animation: 'fadeIn 300ms cubic-bezier(0.23, 1, 0.32, 1) 100ms backwards'
                  }}
                >
                  Selecciona una pregunta d'exemple o escriu la teva pròpia
                </p>
                <div className="grid grid-cols-1 gap-3 w-full max-w-2xl">
                  {prompts.map((prompt, index) => (
                    <button
                      key={index}
                      type="button"
                      className="group text-left text-sm bg-white hover:bg-gray-50 active:scale-[0.98] px-5 py-4 rounded-xl border border-gray-200 text-gray-700 hover:text-gray-900 hover:border-[#9DD566]/30 hover:shadow-md will-change-transform transition-all"
                      style={{
                        animation: `fadeInUp 300ms cubic-bezier(0.23, 1, 0.32, 1) ${200 + index * 50}ms backwards`,
                        transition: 'all 150ms ease, transform 100ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                      }}
                      onClick={() => handlePromptClick(prompt)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#9DD566]/10 to-[#8bc455]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:from-[#9DD566]/20 group-hover:to-[#8bc455]/20 transition-colors">
                          <span className="text-xs font-semibold text-[#9DD566]">?</span>
                        </div>
                        <span className="flex-1 leading-relaxed">{prompt}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4 max-w-3xl mx-auto w-full">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`rounded-2xl will-change-transform ${
                      message.role === "user"
                        ? "bg-gradient-to-r from-[#9DD566] to-[#8bc455] text-white ml-auto max-w-[85%] px-5 py-3.5 shadow-md"
                        : "bg-white text-gray-900 mr-auto max-w-[90%] px-5 py-4 shadow-md border border-gray-100"
                    }`}
                    style={{
                      animation: message.role === "user" 
                        ? 'slideInRight 200ms cubic-bezier(0.23, 1, 0.32, 1) backwards'
                        : 'slideInLeft 200ms cubic-bezier(0.23, 1, 0.32, 1) 100ms backwards'
                    }}
                  >
                    {message.role === "user" ? (
                      <p className="text-sm leading-relaxed font-medium">{message.content}</p>
                    ) : (
                      <MarkdownRenderer content={message.content} />
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div 
                    className="bg-white text-gray-900 mr-auto max-w-[90%] rounded-2xl shadow-md border border-gray-100"
                    style={{
                      animation: 'fadeIn 200ms cubic-bezier(0.23, 1, 0.32, 1) backwards'
                    }}
                  >
                    <LoadingDots />
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="flex gap-2 sm:gap-3 flex-shrink-0 bg-white p-2 sm:p-3 lg:p-4 rounded-xl sm:rounded-2xl border border-gray-200 shadow-lg max-w-3xl mx-auto w-full">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escriu la teva pregunta..."
              disabled={isStreaming}
              className="flex-1 min-w-0 px-3 py-2.5 sm:px-4 sm:py-3 lg:px-5 lg:py-3.5 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9DD566]/50 focus:border-[#9DD566] focus:bg-white text-sm placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                transition: 'border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease'
              }}
              aria-label="Input de l'assistent"
            />
            <button
              type="button"
              onClick={isStreaming ? handleStopStreaming : handleSendMessage}
              disabled={!isStreaming && (isLoading || !inputValue.trim())}
              className="bg-gradient-to-r from-[#9DD566] to-[#8bc455] text-white px-4 py-2.5 sm:px-5 sm:py-3 lg:px-7 lg:py-3.5 rounded-lg sm:rounded-xl hover:shadow-lg active:scale-[0.97] font-semibold text-sm will-change-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex-shrink-0 flex items-center justify-center"
              style={{
                transition: 'all 150ms ease, transform 100ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
              aria-label={isStreaming ? "Aturar generació" : "Enviar missatge"}
            >
              {isStreaming ? <Square className="w-4 h-4" fill="currentColor" /> : "Enviar"}
            </button>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Dades del gràfic de reserves bancàries de la Fed
const fedReservesData = [
  { year: 2000, value: 0.01 }, { year: 2001, value: 0.02 }, { year: 2002, value: 0.02 },
  { year: 2003, value: 0.02 }, { year: 2004, value: 0.02 }, { year: 2005, value: 0.02 },
  { year: 2006, value: 0.02 }, { year: 2007, value: 0.02 }, { year: 2008, value: 0.8 },
  { year: 2009, value: 1.1 }, { year: 2010, value: 1.0 }, { year: 2011, value: 1.5 },
  { year: 2012, value: 1.5 }, { year: 2013, value: 2.4 }, { year: 2014, value: 2.7 },
  { year: 2015, value: 2.5 }, { year: 2016, value: 2.3 }, { year: 2017, value: 2.2 },
  { year: 2018, value: 1.7 }, { year: 2019, value: 1.5 }, { year: 2020, value: 3.2 },
  { year: 2021, value: 4.1 }, { year: 2022, value: 3.3 }, { year: 2023, value: 3.2 },
  { year: 2024, value: 3.4 }, { year: 2025, value: 3.1 }, { year: 2026, value: 2.9 },
];

// Component del gràfic interactiu
const FedReservesChart = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">El Règim Actual</h4>
        <p className="text-sm text-gray-600">Reserves bancàries a la Reserva Federal</p>
      </div>
      <div className="h-[300px] md:h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={fedReservesData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) {
                setActiveIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#5BA4A4" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#5BA4A4" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="year" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => value === 2000 ? '2000' : `'${String(value).slice(-2)}`}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => `$${value}`}
              domain={[0, 4.5]}
              ticks={[0, 0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5]}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900">{payload[0].payload.year}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold text-[#5BA4A4]">${payload[0].value} bilions</span>
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#5BA4A4"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorValue)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-500 mt-2">Font: Reserva Federal</p>
    </div>
  );
};

// Component reutilitzable per als botons d'IA
const AskAIButtons = ({ contentRef }: { contentRef: React.RefObject<HTMLDivElement | null> }) => {
  const handleAskAI = (service: "chatgpt" | "claude") => {
    const clone = contentRef.current?.cloneNode(true) as HTMLElement;
    // Remove the AskAI buttons section from the cloned content
    const askAiSection = clone?.querySelector(".ask-ai-section");
    if (askAiSection) askAiSection.remove();
    // Remove the back button and category badge so text starts from the title
    const backButton = clone?.querySelector("button");
    if (backButton) backButton.remove();
    const categoryDiv = clone?.querySelector("header > div:first-child");
    if (categoryDiv) categoryDiv.remove();
    // Replace chart containers with just their title + [gràfic]
    clone?.querySelectorAll(".chart-container").forEach((chart) => {
      const title = chart.querySelector("h4")?.innerText || "";
      const subtitle = chart.querySelector("p")?.innerText || "";
      const label = [title, subtitle].filter(Boolean).join(" — ");
      chart.textContent = `${label} [gràfic]`;
    });
    let text = clone?.innerText || "";
    const prompt = `Vull saber més sobre aquest article:\n\n${text}`;
    let encodedPrompt = encodeURIComponent(prompt);

    // ChatGPT returns HTTP 431 if URL exceeds ~8KB — truncate by encoded size
    const maxEncodedLength = 7800;
    if (service === "chatgpt" && encodedPrompt.length > maxEncodedLength) {
      // Trim raw text until encoded prompt fits
      const overhead = encodeURIComponent(`Vull saber més sobre aquest article:\n\n`).length;
      let end = text.length;
      while (encodeURIComponent(text.substring(0, end)).length + overhead > maxEncodedLength && end > 0) {
        end -= 100;
      }
      text = text.substring(0, end) + "…";
      encodedPrompt = encodeURIComponent(`Vull saber més sobre aquest article:\n\n${text}`);
    }

    const url =
      service === "chatgpt"
        ? `https://chatgpt.com/?prompt=${encodedPrompt}`
        : `https://claude.ai/new?q=${encodedPrompt}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="ask-ai-section mt-8 pt-6 border-t border-gray-200">
      <p className="text-sm text-gray-500 mb-3">Vols saber-ne més sobre aquest article?</p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => handleAskAI("chatgpt")}
          className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors"
          aria-label="Preguntar a ChatGPT sobre aquest article"
        >
          <img src="/chatgpt-logo.webp" alt="ChatGPT" className="w-5 h-5" />
          <span>Preguntar a ChatGPT</span>
        </button>
        <button
          type="button"
          onClick={() => handleAskAI("claude")}
          className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors"
          aria-label="Preguntar a Claude sobre aquest article"
        >
          <img src="/claude-logo.png" alt="Claude" className="w-5 h-5" />
          <span>Preguntar a Claude</span>
        </button>
      </div>
    </div>
  );
};

// Article complet sobre Kevin Warsh i la Fed
const WarshArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Fed / Política Monetària
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          Dir que vols reduir la Fed és una cosa. Fer-ho és una altra.
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          La visió de "canvi de règim" de Kevin Warsh al banc central podria significar temps més difícils per als mercats.
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-02" className="font-medium">
            2 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>5 min de lectura</span>
        </div>
      </header>

      <div className="w-full aspect-video overflow-hidden rounded-lg mb-12">
        <img 
          src="/kevin-warsh.jpg" 
          alt="Kevin Warsh a la Hoover Institution"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Durant anys, la Fed ha mantingut Wall Street còmode. Però això podria estar a punt de canviar.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Wall Street i la Reserva Federal tenen una llarga història junts. Per combatre la crisi financera global del 2008, 
          la Fed va començar a comprar bons hipotecaris i del Tresor per ajudar a reduir els costos d'endeutament a llarg 
          termini i estabilitzar encara més l'economia. Va tornar a aquesta estratègia durant la pandèmia de Covid-19, i 
          Wall Street va florir el 2021. Avui, el balanç de la Fed supera els 6,5 bilions de dòlars. Els seus passius 
          inclouen uns 3 bilions en forma de reserves—essencialment, dipòsits dels bancs a la Fed.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La nova elecció del president Trump com a nominat per liderar la Fed, <strong>Kevin Warsh</strong>, era governador 
          de la Fed durant la crisi del 2008. En una entrevista recent a la Hoover Institution va dir que va donar suport 
          a la ronda inicial del que es va conèixer com a relaxació quantitativa. Però també va advertir el 2010 que continuar 
          expandint el balanç de la Fed "no era una opció gratuïta" i comportava "riscos significatius".
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Avui, aquesta visió segueix sent una part clau del seu argument per a tipus d'interès més baixos. Ha escrit que 
          el balanç de la Fed està "inflat", i que la seva expansió ha estat un dels principals motors de la inflació. 
          Reduir-lo, argumenta, podria permetre tipus més baixos.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            "Si féssim funcionar la impremta una mica més silenciosament, podríem tenir tipus d'interès més baixos. 
            Perquè el que estem fent ara és inundar el sistema amb tots aquests diners, cosa que fa que la inflació 
            estigui per sobre de l'objectiu."
          </p>
          <cite className="text-sm text-gray-500 mt-2 block">— Kevin Warsh, Hoover Institution</cite>
        </blockquote>

        <FedReservesChart />

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La Fed ja ha estat reduint el seu balanç, que ha baixat des del seu màxim de gairebé 9 bilions el 2022. 
          Però també hi ha canvis molt més amplis que es podrien considerar.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">El règim de "reserves abundants"</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El règim actual de "reserves abundants" de la Fed ha canviat fonamentalment la política monetària i la banca. 
          Des del 2008, la Fed ha utilitzat l'interès que paga als bancs sobre les seves reserves per pujar o baixar 
          l'anomenat "sòl" dels tipus d'interès, un canvi respecte a altres eines que utilitzava abans per influir 
          en els tipus de mercat.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Els bancs i els mercats també s'han acostumat a operar en aquesta era de liquiditat abundant. Alguns economistes 
          han vinculat el creixement dels dipòsits no assegurats dels bancs en els últims anys a la relaxació quantitativa. 
          Els bancs depenen dels ingressos per interessos generats per les seves reserves.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            "Els diners a Wall Street són massa fàcils, i el crèdit a Main Street és massa estret. La generositat de 
            la Fed es pot redesplegar en forma de tipus d'interès més baixos per donar suport a les famílies i les 
            petites i mitjanes empreses."
          </p>
          <cite className="text-sm text-gray-500 mt-2 block">— Kevin Warsh</cite>
        </blockquote>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Els riscos del "quantitative tightening"</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però fins ara, quan la Fed ha intentat permetre que el seu balanç es redueixi, sovint anomenat "enduriment 
          quantitatiu", en algun moment això ha causat pertorbacions als mercats. El 2019, després d'un període de 
          reducció del balanç, un pic breu en els anomenats tipus de finançament repo va alarmar banquers i inversors. 
          La reducció es va reprendre després de la Covid. Però els funcionaris de la Fed van citar riscos per als 
          mercats monetaris a finals de l'any passat quan van decidir aturar de nou el procés de reducció.
        </p>

        <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6 my-6">
          <p className="text-gray-700 leading-relaxed">
            <strong>"Voler un balanç de la Reserva Federal més petit és molt diferent d'aconseguir-ne un pràcticament"</strong>, 
            van escriure els estrategues de tipus d'interès de SMBC Nikko en una nota. Van assenyalar que quan la demanda 
            de reserves bancàries és superior al que subministra la Fed, es pot tornar més car per als inversors finançar 
            coses com ara compres de títols del Tresor.
          </p>
        </div>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Obstacles pel camí</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Molts banquers i inversors acollirien amb satisfacció un paper més lleuger de la Fed en l'economia i els mercats. 
          Però arribar-hi podria tenir punts conflictius:
        </p>

        <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
          <li>Els banquers podrien oposar-se a la visió d'alguns funcionaris que la Fed hauria de deixar de pagar interessos sobre les reserves.</li>
          <li>La Fed podria animar els bancs a demanar préstecs al banc central més regularment, però aquest préstec pot tenir un estigma per a alguns inversors bancaris.</li>
          <li>Els reguladors de la Fed podrien relaxar o modificar els requisits de liquiditat dels bancs, però els bancs mateixos podrien optar per mantenir-se molt líquids per a la seva pròpia gestió interna del risc.</li>
        </ul>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Així doncs, els detalls més fins de qualsevol pla per remodelar la Fed importaran enormement en com podria afectar 
          els mercats. Alguns inversors ja estan nerviosos, basant-se en la reacció a les notícies de la nominació el divendres, 
          quan les accions van caure, malgrat el potencial de tipus d'interès més baixos.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            "Warsh ha estat vist com a baixista per a les accions, donada la seva visió restrictiva sobre el balanç."
          </p>
          <cite className="text-sm text-gray-500 mt-2 block">— Estrategues de renda variable de Wells Fargo</cite>
        </blockquote>

        <div className="bg-[#9DD566]/10 border-2 border-[#9DD566] rounded-lg p-6 my-8">
          <h3 className="font-semibold text-gray-900 mb-2">La conclusió</h3>
          <p className="text-gray-700">
            Desconnectar-se de l'era dels diners fàcils podria portar alguns mals de cap pel camí. El que queda clar és que 
            el que Warsh proposa no és un simple ajustament tècnic, sinó un canvi de règim que podria redefinir la relació 
            entre la Fed, els bancs i els mercats financers tal com la coneixem.
          </p>
        </div>
      </div>

      <footer className="mt-12 pt-8 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Adaptat de l'article original de Telis Demos al Wall Street Journal, 2 de febrer de 2026.
        </p>
      </footer>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

// Dades del gràfic del preu d'Oracle (març 2025 - febrer 2026)
const oracleSharePriceData = [
  { date: "Mar 2025", price: 165 },
  { date: "Abr 2025", price: 175 },
  { date: "Mai 2025", price: 170 },
  { date: "Jun 2025", price: 155 },
  { date: "Jul 2025", price: 145 },
  { date: "Ago 2025", price: 140 },
  { date: "Set 4", price: 238 }, // Before RPO jump
  { date: "Set 9", price: 325 }, // RPO jump - peak
  { date: "Set 16", price: 300 },
  { date: "Set 23", price: 320 },
  { date: "Set 30", price: 310 },
  { date: "Oct 2025", price: 280 },
  { date: "Nov 2025", price: 290 },
  { date: "Des 2025", price: 305 },
  { date: "Gen 2026", price: 270 },
  { date: "Feb 2", price: 155 }, // Current price
];

// Component del gràfic interactiu d'Oracle
const OracleSharePriceChart = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const rpoJumpIndex = oracleSharePriceData.findIndex(d => d.date === "Set 9");

  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Preu de l'acció d'Oracle</h4>
        <p className="text-sm text-gray-600">Salt després de l'anunci de les RPOs</p>
      </div>
      <div className="h-[300px] md:h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={oracleSharePriceData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) {
                setActiveIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <defs>
              <linearGradient id="colorOraclePrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#5BA4A4" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#5BA4A4" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="date" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              interval="preserveStartEnd"
              ticks={["Mar 2025", "Feb 2"]}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => `$${value}`}
              domain={[100, 350]}
              ticks={[100, 125, 150, 175, 200, 225, 250, 275, 300, 325, 350]}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const isRpoJump = payload[0].payload.date === "Set 9";
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900">{payload[0].payload.date}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold text-[#5BA4A4]">${payload[0].value}</span>
                      </p>
                      {isRpoJump && (
                        <p className="text-xs text-[#9DD566] mt-1 font-medium">Salt d'RPOs reportat</p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke="#5BA4A4"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorOraclePrice)"
            />
            {/* Línia vertical per marcar el salt d'RPOs */}
            {rpoJumpIndex > 0 && (
              <line
                x1={`${(rpoJumpIndex / (oracleSharePriceData.length - 1)) * 100}%`}
                y1="0"
                x2={`${(rpoJumpIndex / (oracleSharePriceData.length - 1)) * 100}%`}
                y2="100%"
                stroke="#9DD566"
                strokeWidth={2}
                strokeDasharray="5,5"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Nota: RPO = obligacions de rendiment pendents (remaining performance obligations)
        <br />
        Font: FactSet
      </p>
    </div>
  );
};

// Article sobre Oracle i OpenAI
const OracleArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Tech / Cloud
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          Què arrisca Oracle amb la relació entre OpenAI i Nvidia
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          El gir de Nvidia amb OpenAI posa la comptabilitat d'Oracle sota els focus. I les xifres no pinten gaire bé.
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-02" className="font-medium">
            2 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>5 min de lectura</span>
        </div>
      </header>

      <div className="w-full aspect-video overflow-hidden rounded-lg mb-12">
        <img 
          src="/oracle-sign.jpg" 
          alt="Oficines corporatives d'Oracle"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Que Nvidia inverteixi molt menys dels 100.000 milions de dòlars previstos en OpenAI planteja un problema seriós per a Oracle.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La pregunta principal és si OpenAI podrà complir el seu contracte de 300.000 milions de dòlars amb Oracle, i si Oracle 
          hauria d'estar registrant aquesta quantitat sencera als seus llibres comptables.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          A 30 de novembre, Oracle va reportar 523.000 milions de dòlars en obligacions de rendiment pendents (RPOs). Això representa 
          vendes contractades però encara no reconegudes com a ingressos. La xifra, una nota a peu de pàgina que els inversors 
          segueixen de prop, era unes nou vegades els ingressos d'Oracle dels últims quatre trimestres, i incloïa els 300.000 milions 
          relacionats amb OpenAI.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Les accions d'Oracle es van disparar al setembre, pujant un 36% en un sol dia després d'un informe de resultats que mostrava 
          que les seves RPOs s'havien més que triplicat respecte al trimestre anterior.
        </p>

        <OracleSharePriceChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">El problema de la circularitat</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La relació d'OpenAI amb Nvidia ha simbolitzat les preocupacions sobre la circularitat en els acords d'intel·ligència artificial. 
          La carta d'intencions entre ambdues empreses, anunciada el 22 de setembre, demanava que Nvidia invertís fins a 100.000 milions 
          de dòlars en OpenAI al llarg de molts anys. Part d'aquests diners tornarien a Nvidia per comprar els seus xips, mentre que 
          una altra part ajudaria OpenAI a pagar coses com el seu acord amb Oracle per proveir capacitat de computació.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Davant les preocupacions sobre els seus nivells de deute i la fiabilitat d'OpenAI, Oracle s'ha enfrontat a demandes per 
          augmentar el seu capital social per mantenir la seva qualificació creditícia d'inversió. L'empresa sembla estar responent.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          L'1 de febrer, Oracle va anunciar que planeja emetre fins a 20.000 milions de dòlars en accions ordinàries aquest any, 
          com a part d'un pla més ampli per recaptar entre 45.000 i 50.000 milions de dòlars mitjançant finançament per capital 
          i deute per expandir el seu negoci d'infraestructura al núvol.
        </p>

        <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6 my-6">
          <p className="text-gray-700 leading-relaxed">
            Emetre accions diluirà els accionistes, després que ja hagin vist les seves accions perdre la meitat del seu valor 
            des del pic de setembre. Però amb la incertesa i circularitat dels acords d'IA sent el que són, reforçar el coixí 
            de capital de l'empresa sembla prudent.
          </p>
        </div>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Oracle ja està construint</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Per la seva banda, Oracle ja està construint centres de dades, finançats amb nou deute, basant-se en el contracte 
          que va signar amb OpenAI. Oracle i Nvidia també són grans clients l'un de l'altre, tot i que Oracle recentment ha 
          senyalat que diversificaria les seves compres de xips d'IA més enllà de Nvidia.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Ara una gran part del finançament previst d'OpenAI està en dubte. Això planteja noves preguntes sobre com OpenAI 
          pagarà els seus 1,4 bilions de dòlars estimats en compromisos, inclosos els que té amb Oracle. Això dependrà de 
          la seva capacitat per captar diners d'inversors i augmentar els ingressos dramàticament.
        </p>

        <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6 my-6">
          <p className="text-gray-700 leading-relaxed">
            Una portaveu d'Oracle, Deborah Hellinger, va dir: <strong>"L'acord entre Nvidia i OpenAI té zero impacte en 
            la nostra relació financera amb OpenAI. Continuem confiant plenament en la capacitat d'OpenAI per recaptar 
            fons i complir els seus compromisos."</strong>
          </p>
        </div>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Nvidia redueix el compromís</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          The Wall Street Journal va informar el 30 de gener que l'acord de setembre amb Nvidia s'havia estancat, després 
          que algunes persones dins de Nvidia expressessin dubtes sobre l'acord. L'endemà, el CEO de Nvidia, Jensen Huang, 
          va dir als periodistes a Taipei que el gegant dels xips "absolutament estaria involucrat" en l'última ronda de 
          finançament d'OpenAI. Quan se li va preguntar si la quantitat seria superior a 100.000 milions de dòlars, va dir: 
          "No, no, res d'això."
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          OpenAI està buscant 100.000 milions de dòlars per a la seva última ronda de finançament, que podria incloure Nvidia. 
          SoftBank Group està en negociacions per invertir fins a 30.000 milions de dòlars més, després d'augmentar la seva 
          participació a l'11% al desembre quan va invertir 22.500 milions. Amazon.com també està en converses per invertir 
          en la ronda, i OpenAI té com a objectiu una oferta pública inicial al quart trimestre d'aquest any.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">La pregunta comptable</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          És possible que tot això succeeixi i que OpenAI pugui recaptar les enormes sumes de diners que necessita. O si 
          OpenAI no pot pagar els 300.000 milions complets, potser un altre client podria intervenir per omplir el seu lloc. 
          Però tot això és especulatiu, i la notícia que Nvidia està reduint el seu compromís amb OpenAI planteja noves 
          preguntes sobre la probabilitat que els 300.000 milions es materialitzin.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Això significa que Oracle tindrà una pregunta urgent sobre els seus propis llibres: <strong>És encara "probable" 
          cobrar els 300.000 milions de dòlars d'OpenAI segons el judici de la direcció?</strong> La resposta ha de ser sí, 
          segons les normes comptables, perquè Oracle inclogui la suma en les seves RPOs.
        </p>

        <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6 my-6">
          <p className="text-gray-700 leading-relaxed italic">
            Com la direcció respon a aquesta pregunta en els seus pròxims informes financers, i com respon el mercat, 
            podria tenir efectes en cascada.
          </p>
        </div>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La qualificació creditícia d'inversió d'Oracle, ara en triple-B, està sota vigilància negativa a S&P i Moody's, 
          senyalant una possible rebaixa. Part del deute d'Oracle, inclòs un bons a 10 anys venut al setembre, recentment 
          s'ha estat negociant com a bons escombraria, i el cost per protegir el deute d'Oracle contra impagaments ha pujat.
        </p>

        <div className="bg-[#9DD566]/10 border-2 border-[#9DD566] rounded-lg p-6 my-8">
          <h3 className="font-semibold text-gray-900 mb-2">La conclusió</h3>
          <p className="text-gray-700">
            El que Oracle no es pot permetre sacrificar és la credibilitat. Si la direcció de l'empresa continua concloent 
            que la cobrabilitat d'OpenAI és probable, aquesta valoració més val que sigui correcta. Perquè si no ho és, 
            el mercat passarà factura. I de quina manera.
          </p>
        </div>
      </div>

      <footer className="mt-12 pt-8 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Adaptat de l'article original de Jonathan Weil al Wall Street Journal, 2 de febrer de 2026.
        </p>
      </footer>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

// Dades per al gràfic de despeses de capital
const capitalExpendituresData = [
  { year: '2020', Google: 16, Meta: 10 },
  { year: "'21", Google: 20, Meta: 14 },
  { year: "'22", Google: 25, Meta: 27 },
  { year: "'23", Google: 27, Meta: 23 },
  { year: "'24", Google: 48, Meta: 33 },
  { year: "'25", Google: 87, Meta: 67 },
  { year: "'26", Google: 185, Meta: 130 },
];

// Component del gràfic de despeses de capital
const CapitalExpendituresChart = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Spending Race</h4>
        <p className="text-sm text-gray-600">Capital expenditures per year</p>
      </div>
      <div className="h-[300px] md:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={capitalExpendituresData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) {
                setActiveIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <XAxis
              dataKey="year"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => `$${value}`}
              domain={[0, 200]}
              ticks={[0, 25, 50, 75, 100, 125, 150, 175, 200]}
              label={{ value: '$200 billion', position: 'top', offset: 10, style: { fontSize: 12, fill: '#6B7280' } }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900 mb-2">{payload[0].payload.year}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#5BA4A4' }}>Google: </span>
                        ${payload[0].payload.Google}B
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#4B5563' }}>Meta: </span>
                        ${payload[0].payload.Meta}B
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
              formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
            />
            <Bar dataKey="Google" fill="#5BA4A4" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Meta" fill="#4B5563" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 space-y-1">
        <p className="text-xs text-gray-500">Note: 2026 is high end of company projections</p>
        <p className="text-xs text-gray-500">Source: company data</p>
      </div>
    </div>
  );
};

// Dades per al gràfic de rendiment d'accions
const stockPerformanceData = [
  { date: 'Nov 5', Alphabet: 0.5, Amazon: -2, Microsoft: -5 },
  { date: 'Nov 12', Alphabet: 3.5, Amazon: -1, Microsoft: -4 },
  { date: 'Nov 19', Alphabet: 1, Amazon: -0.5, Microsoft: -2 },
  { date: 'Nov 26', Alphabet: 2, Amazon: -2, Microsoft: -1 },
  { date: 'Dec 3', Alphabet: 4, Amazon: -9, Microsoft: -6 },
  { date: 'Dec 10', Alphabet: 15, Amazon: -12, Microsoft: -8 },
  { date: 'Dec 17', Alphabet: 14.5, Amazon: -14, Microsoft: -9 },
  { date: 'Dec 24', Alphabet: 12.5, Amazon: -10, Microsoft: -7 },
  { date: 'Dec 31', Alphabet: 13.5, Amazon: -9, Microsoft: -7.5 },
  { date: 'Gen 7', Alphabet: 14, Amazon: -8, Microsoft: -7 },
  { date: 'Gen 14', Alphabet: 13, Amazon: -7, Microsoft: -8 },
  { date: 'Gen 21', Alphabet: 12, Amazon: -8, Microsoft: -8.5 },
  { date: 'Gen 28', Alphabet: 11.5, Amazon: -7, Microsoft: -7 },
  { date: 'Feb 4', Alphabet: 15.5, Amazon: -2, Microsoft: -7.5 },
  { date: 'Feb 11', Alphabet: 12, Amazon: -8, Microsoft: -9 },
  { date: 'Feb 18', Alphabet: 20, Amazon: -5, Microsoft: -3 },
  { date: 'Feb 25', Alphabet: 18.5, Amazon: -3, Microsoft: -4 },
  { date: 'Mar 4', Alphabet: 15, Amazon: -5, Microsoft: -5 },
  { date: 'Mar 11', Alphabet: 17, Amazon: -8, Microsoft: -7 },
  { date: 'Mar 18', Alphabet: 18, Amazon: -12, Microsoft: -10 },
  { date: 'Mar 25', Alphabet: 19.5, Amazon: -15, Microsoft: -17 },
  { date: 'Abr 1', Alphabet: 20, Amazon: -7, Microsoft: -16 },
  { date: 'Abr 8', Alphabet: 22.5, Amazon: -3, Microsoft: -7 },
  { date: 'Abr 15', Alphabet: 19.5, Amazon: -7, Microsoft: -21 },
];

// Component del gràfic de rendiment d'accions
const StockPerformanceChart = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Clouded</h4>
        <p className="text-sm text-gray-600">Share-price performance, past three months</p>
      </div>
      <div className="h-[350px] md:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={stockPerformanceData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) {
                setActiveIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <XAxis
              dataKey="date"
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              interval="preserveStartEnd"
              ticks={['Dec. 2025', "'26"]}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => `${value}%`}
              domain={[-25, 30]}
              ticks={[-25, -20, -15, -10, -5, 0, 5, 10, 15, 20, 25, 30]}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900 mb-2">{payload[0].payload.date}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#2C5F5F' }}>Alphabet: </span>
                        {payload[0].payload.Alphabet > 0 ? '+' : ''}{payload[0].payload.Alphabet}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#93C5D5' }}>Amazon: </span>
                        {payload[0].payload.Amazon > 0 ? '+' : ''}{payload[0].payload.Amazon}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#5BA4A4' }}>Microsoft: </span>
                        {payload[0].payload.Microsoft > 0 ? '+' : ''}{payload[0].payload.Microsoft}%
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
              formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
            />
            <Line type="monotone" dataKey="Alphabet" stroke="#2C5F5F" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="Amazon" stroke="#93C5D5" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="Microsoft" stroke="#5BA4A4" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-500 mt-2">Source: FactSet</p>
    </div>
  );
};

// Data per Software Index Performance Chart
const softwareIndexData = [
  { date: "Set. 2025", phlx: 0, nasdaq: 0, igv: 0 },
  { date: "Set. 15", phlx: 4, nasdaq: 2, igv: -2 },
  { date: "Set. 30", phlx: 2, nasdaq: 3, igv: -4 },
  { date: "Oct. 1", phlx: 5, nasdaq: 4, igv: -2 },
  { date: "Oct. 10", phlx: 10, nasdaq: 5, igv: 0 },
  { date: "Oct. 20", phlx: 13, nasdaq: 7, igv: 2 },
  { date: "Oct. 30", phlx: 18, nasdaq: 8, igv: 4 },
  { date: "Nov. 1", phlx: 14, nasdaq: 6, igv: 2 },
  { date: "Nov. 10", phlx: 22, nasdaq: 9, igv: 4 },
  { date: "Nov. 20", phlx: 29, nasdaq: 10, igv: 2 },
  { date: "Nov. 30", phlx: 26, nasdaq: 8, igv: -2 },
  { date: "Des. 1", phlx: 22, nasdaq: 6, igv: -4 },
  { date: "Des. 10", phlx: 25, nasdaq: 9, igv: -8 },
  { date: "Des. 20", phlx: 13, nasdaq: 4, igv: -12 },
  { date: "Des. 30", phlx: 28, nasdaq: 10, igv: -4 },
  { date: "Gen. '26", phlx: 32, nasdaq: 11, igv: -2 },
  { date: "Gen. 10", phlx: 27, nasdaq: 10, igv: -5 },
  { date: "Gen. 20", phlx: 26, nasdaq: 9, igv: -8 },
  { date: "Gen. 30", phlx: 28, nasdaq: 10, igv: -6 },
  { date: "Feb. 1", phlx: 36, nasdaq: 11, igv: -5 },
  { date: "Feb. 5", phlx: 42, nasdaq: 12, igv: -10 },
  { date: "Feb. 10", phlx: 40, nasdaq: 10, igv: -15 },
  { date: "Feb. 15", phlx: 47, nasdaq: 11, igv: -18 },
  { date: "Feb. 20", phlx: 48, nasdaq: 9, igv: -22 },
  { date: "Feb.", phlx: 40, nasdaq: 8, igv: -28 },
];

const SoftwareIndexChart = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Soft Target</h4>
        <p className="text-sm text-gray-600">Index performance, past six months</p>
      </div>
      <div className="h-[350px] md:h-[450px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={softwareIndexData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) {
                setActiveIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <XAxis
              dataKey="date"
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              interval="preserveStartEnd"
              ticks={['Set. 2025', 'Oct.', 'Nov.', 'Des.', "Gen. '26", 'Feb.']}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => `${value}%`}
              domain={[-30, 60]}
              ticks={[-30, -20, -10, 0, 10, 20, 30, 40, 50, 60]}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900 mb-2">{payload[0].payload.date}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#0D9488' }}>PHLX Semiconductor: </span>
                        {payload[0].payload.phlx > 0 ? '+' : ''}{payload[0].payload.phlx}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#6B7280' }}>Nasdaq Composite: </span>
                        {payload[0].payload.nasdaq > 0 ? '+' : ''}{payload[0].payload.nasdaq}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#EA580C' }}>IGV Index: </span>
                        {payload[0].payload.igv > 0 ? '+' : ''}{payload[0].payload.igv}%
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              verticalAlign="top"
              height={50}
              iconType="line"
              wrapperStyle={{ paddingBottom: '20px' }}
              formatter={(value) => {
                const labels: { [key: string]: string } = {
                  phlx: 'PHLX Semiconductor Index',
                  nasdaq: 'Nasdaq Composite',
                  igv: 'IGV Index'
                };
                return <span style={{ fontSize: '13px', color: '#374151' }}>{labels[value]}</span>;
              }}
            />
            <Line
              type="monotone"
              dataKey="phlx"
              stroke="#0D9488"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5, fill: '#0D9488' }}
            />
            <Line
              type="monotone"
              dataKey="nasdaq"
              stroke="#6B7280"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5, fill: '#6B7280' }}
            />
            <Line
              type="monotone"
              dataKey="igv"
              stroke="#EA580C"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5, fill: '#EA580C' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-500 mt-4">
        <span className="font-medium">Note:</span> IGV refers to the iShares Expanded Tech-Software Sector ETF.
      </p>
      <p className="text-xs text-gray-500 mt-1">Source: FactSet</p>
    </div>
  );
};

// Article sobre IA i programari
const IAProgramariArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Tech / IA
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          La IA no matarà el negoci del programari, només la seva història de creixement
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          Les pors que les empreses de programari s'enfrontin a un esdeveniment d'extinció són exagerades, però altres perills són ben reals.
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-04" className="font-medium">
            4 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>Per Dan Gallagher</span>
        </div>
      </header>

      <div className="w-full aspect-video overflow-hidden rounded-lg mb-12">
        <img
          src="/software-ia.jpg"
          alt="Intel·ligència artificial i programari"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La intel·ligència artificial no destruirà el negoci del programari. Però la creença persistent que ho farà pot causar igualment molt de mal.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          I aquest mal ja s'ha fet. Les accions de programari han estat en caiguda lliure durant diversos mesos, i la venda massiva ha agafat velocitat els darrers dies. L'índex IGV Software ha baixat al voltant d'un 30% des del seu màxim a finals de setembre, un descens marcat per una brutal venda massiva dimarts passat.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">L'anunci d'Anthropic</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El darrer moviment va ser desencadenat per l'anunci d'Anthropic de noves funcionalitats per al seu assistent Claude Cowork. Aquestes noves funcions estan dirigides a usuaris del sector legal i estan dissenyades per automatitzar processos com la revisió de contractes i els informes jurídics.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          L'anunci d'Anthropic inicialment va provocar una venda massiva d'empreses editorials orientades al mercat legal. Ràpidament va alimentar una narrativa persistent sobre el potencial de les eines d'IA per pertorbar els negocis de programari establerts. Grans noms del programari com Salesforce, ServiceNow, Adobe i Workday van caure un 7% dimarts, mentre que Intuit va baixar gairebé un 11%.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Pors exagerades</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          N'hi ha prou amb això? La creença que les grans corporacions substituiran plataformes de programari altament complexes amb aplicacions codificades per IA és exagerada.
        </p>

        <SoftwareIndexChart />

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Aquestes plataformes executen tasques crítiques com la nòmina i la gestió de TI, i requereixen una profunda experiència en la matèria que va molt més enllà de la codificació real del programari. Fins i tot el principal facilitador de la indústria de la IA actual sembla estar-hi d'acord.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            "Hi ha aquesta noció que la indústria del programari està en declivi i serà substituïda per la IA. És la cosa més il·lògica del món, i el temps ho demostrarà." — Jensen Huang, CEO de Nvidia
          </p>
        </blockquote>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Mercat volàtil</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però el temps no espera en un mercat volàtil que està ansiós per trobar senyals de pertorbació per part de la IA. A més de tot plegat, les empreses de programari prèviament vistes com a guanyadores de la IA han publicat recentment informes trimestrals decebedors que projecten un núvol encara més fosc sobre el sector.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          En un informe de dimarts, l'analista de KeyBanc Jackson Ader va assenyalar que els informes de la setmana passada de Microsoft, ServiceNow i SAP "van ser deslluïts o directament decebedors, cosa que significa que potser no es pot confiar del tot en l'estratègia de fugida cap a la qualitat".
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Complicada posició</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Els proveïdors de programari ara es troben en la complicada posició d'haver de refutar una negativa. Mostrar una acceleració en el creixement dels ingressos ajudaria a combatre les pors de la pertorbació de la IA.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però això serà difícil en una època de restricció de la despesa corporativa i reduccions massives de la força laboral. Amazon, United Parcel Service i Pinterest van anunciar només la setmana passada plans per eliminar fins a 46.700 llocs de treball.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Les acomiadaments poden afectar el nombre de "llicències" que sustenten molts contractes de programari al núvol. Els grans clients a qui venen les empreses de programari també estan invertint en els seus propis projectes interns d'IA que potser no estan dissenyats per substituir les plataformes de programari que utilitzen, però encara poden consumir dòlars del pressupost de TI i atenció de la direcció.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Pressió sobre els marges</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Com a mínim, això podria donar als clients un avantatge addicional en les negociacions de renovació de contractes, especialment perquè molts proveïdors de programari acaben de tancar els seus exercicis fiscals al gener quan les pors de pertorbació de la IA estaven en plena escalada. Això planteja la possibilitat d'una pressió accentuada sobre els marges i, al seu torn, sobre el creixement.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La venda massiva podria obrir la porta a compradors de capital privat a qui els agrada adquirir empreses de programari. D'altra banda, moltes d'aquestes firmes es van veure elles mateixes atrapades en la venda massiva de dimarts a causa de la seva exposició existent al programari. Potser no estan gaire ansioses d'augmentar-la ara.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Valoracions compreses</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Les valoracions certament s'han comprimit; el múltiple mitjà de les empreses de l'índex IGV ha caigut de 39 vegades els beneficis futurs a unes 21 vegades ara, segons dades de FactSet. Però aquestes xifres es basen principalment en estimacions d'analistes que exclouen la compensació basada en accions, cosa que pot ser enganyosa en una indústria que en fa servir tanta.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            La IA no eliminarà la necessitat de plataformes de programari especialitzades. Però la mera supervivència no és suficient per recuperar les pèrdues recents.
          </p>
        </blockquote>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La IA no eliminarà la necessitat de plataformes de programari especialitzades. Però la mera supervivència no és suficient per recuperar les pèrdues recents.
        </p>
      </div>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

// Dades del gràfic de retallades laborals per IA
const aiLayoffsData = [
  { name: 'Acomiadaments moderats en anticipació de la IA', value: 39, color: '#5B9BD5' },
  { name: 'Contractant menys gent en anticipació de la IA', value: 29, color: '#1B3A6B' },
  { name: 'Grans acomiadaments en anticipació de la IA', value: 21, color: '#4A7FC4' },
  { name: 'No segurs sobre acomiadaments per IA', value: 9, color: '#C8C8C8' },
  { name: 'Grans acomiadaments per implementació real d\'IA', value: 2, color: '#E87D2F' },
];

const RADIAN = Math.PI / 180;

const AILayoffsChart = () => {
  const [activeSlice, setActiveSlice] = useState<number | null>(null);

  const renderCustomLabel = ({
    cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value
  }: {
    cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number;
    percent: number; index: number; name: string; value: number;
  }) => {
    const radius = outerRadius + 16;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const textAnchor = x > cx ? 'start' : 'end';

    // Line from pie to label
    const lineEndX = cx + (outerRadius + 6) * Math.cos(-midAngle * RADIAN);
    const lineEndY = cy + (outerRadius + 6) * Math.sin(-midAngle * RADIAN);

    return (
      <g>
        <line x1={lineEndX} y1={lineEndY} x2={x} y2={y} stroke={aiLayoffsData[index].color} strokeWidth={1} />
        <text
          x={x + (x > cx ? 4 : -4)}
          y={y}
          textAnchor={textAnchor}
          dominantBaseline="central"
          style={{ fontSize: '11px', fill: aiLayoffsData[index].color === '#C8C8C8' ? '#6B7280' : aiLayoffsData[index].color, fontWeight: 600 }}
        >
          {`${value}%`}
        </text>
      </g>
    );
  };

  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-2">
        <h4 className="text-lg font-bold text-gray-900">Les empreses retallen llocs de treball per la IA que esperen — no per la que tenen</h4>
        <p className="text-sm text-gray-600 mt-1">Com responen les empreses a l'impacte de la IA en la plantilla [%]</p>
      </div>
      <div className="h-[320px] md:h-[380px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={aiLayoffsData}
              cx="50%"
              cy="50%"
              outerRadius="38%"
              dataKey="value"
              label={renderCustomLabel}
              labelLine={false}
              onMouseEnter={(_, index) => setActiveSlice(index)}
              onMouseLeave={() => setActiveSlice(null)}
              stroke="#fff"
              strokeWidth={2}
            >
              {aiLayoffsData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  opacity={activeSlice !== null && activeSlice !== index ? 0.5 : 1}
                  style={{ transition: 'opacity 0.2s', cursor: 'pointer' }}
                />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-[220px]">
                      <p className="text-sm font-semibold text-gray-900 mb-1">{data.value}%</p>
                      <p className="text-sm text-gray-600">{data.name}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1.5 mb-4">
        {aiLayoffsData.map((entry, index) => (
          <div key={index} className="flex items-start gap-2 text-xs text-gray-700">
            <span className="inline-block w-3 h-3 rounded-sm mt-0.5 flex-shrink-0" style={{ backgroundColor: entry.color }}></span>
            <span>{entry.name} ({entry.value}%)</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500">Basat en una enquesta a 1.006 executius globals el desembre de 2025, realitzada per Thomas H. Davenport i Laks Srinivasan</p>
      <p className="text-xs text-gray-500 mt-1">Source: Harvard Business Review</p>
    </div>
  );
};

// Dades dels índexs borsaris setmanals (Feb 2-6, 2026) amb soroll realista
const stockIndexWeekData = (() => {
  let seed = 73;
  const rand = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  const n = (s: number) => (rand() - 0.5) * s;

  // 13 intervals per day (every 30 min from 9:30 to 16:00), 5 days = 65 points
  const days = [
    // [dayLabel, dow_trajectory, sp500_trajectory, nasdaq_trajectory]
    // Feb 2 (Mon) - Dow up, S&P flat-positive, Nasdaq slight negative
    { day: 2, dow: [0.4,0.55,0.7,0.95,1.1,1.2,1.05,0.95,1.1,1.2,1.05,0.85,0.8],
              sp: [0.15,0.25,0.4,0.55,0.65,0.5,0.35,0.45,0.55,0.6,0.45,0.3,0.25],
              nq: [0.05,-0.05,-0.15,-0.25,-0.1,-0.3,-0.5,-0.35,-0.2,-0.35,-0.55,-0.65,-0.55] },
    // Feb 3 (Tue) - Dow peaks then dips, S&P turns negative, Nasdaq slides
    { day: 3, dow: [0.95,1.15,1.3,1.35,1.25,1.1,0.95,0.85,0.7,0.55,0.5,0.55,0.6],
              sp: [0.3,0.45,0.55,0.6,0.45,0.3,0.15,0.0,-0.15,-0.25,-0.25,-0.15,-0.1],
              nq: [-0.4,-0.25,-0.15,-0.3,-0.55,-0.7,-0.9,-1.1,-1.25,-1.4,-1.5,-1.35,-1.25] },
    // Feb 4 (Wed) - BIG sell-off: sharp drops, partial late recovery
    { day: 4, dow: [0.3,0.15,0.0,-0.15,-0.2,-0.3,-0.4,-0.5,-0.45,-0.35,-0.25,-0.1,0.0],
              sp: [-0.3,-0.5,-0.7,-0.9,-1.1,-1.5,-1.8,-2.0,-1.85,-1.65,-1.5,-1.2,-1.05],
              nq: [-1.5,-1.8,-2.1,-2.5,-2.8,-3.2,-3.6,-4.0,-4.35,-4.0,-3.75,-3.3,-2.9] },
    // Feb 5 (Thu) - choppy recovery
    { day: 5, dow: [0.15,0.3,0.5,0.55,0.4,0.5,0.6,0.7,0.65,0.8,0.95,1.1,1.2],
              sp: [-0.85,-0.7,-0.55,-0.45,-0.6,-0.5,-0.4,-0.45,-0.55,-0.4,-0.3,-0.15,-0.1],
              nq: [-2.7,-2.5,-2.3,-2.45,-2.6,-2.4,-2.3,-2.2,-2.35,-2.15,-2.0,-1.75,-1.7] },
    // Feb 6 (Fri) - Dow surges, S&P near zero, Nasdaq still down
    { day: 6, dow: [1.3,1.25,1.4,1.55,1.6,1.7,1.75,1.85,2.0,2.15,2.25,2.35,2.4],
              sp: [-0.15,-0.2,-0.1,-0.1,-0.1,-0.15,-0.2,-0.15,-0.1,-0.1,-0.15,-0.15,-0.2],
              nq: [-1.8,-1.9,-1.85,-2.1,-2.15,-2.25,-2.3,-2.2,-2.1,-1.95,-1.9,-1.85,-1.8] },
  ];

  const data: { idx: number; time: string; dow: number; sp500: number; nasdaq: number }[] = [];
  let idx = 0;
  const times = ['9:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','16:00'];

  days.forEach(({ day, dow, sp, nq }) => {
    times.forEach((t, j) => {
      data.push({
        idx: idx++,
        time: `Feb ${day} ${t}`,
        dow: Math.round((dow[j] + n(0.08)) * 100) / 100,
        sp500: Math.round((sp[j] + n(0.06)) * 100) / 100,
        nasdaq: Math.round((nq[j] + n(0.1)) * 100) / 100,
      });
    });
  });
  return data;
})();

// Index positions for X-axis tick labels
const stockXTicks = [0, 39]; // Feb. 2026 at start, Feb. 6 at day 5 start

const StockIndexWeekChart = () => {
  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Índexs borsaris dels EUA, aquesta setmana</h4>
      </div>
      <div className="h-[350px] md:h-[450px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={stockIndexWeekData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <XAxis
              dataKey="idx"
              type="number"
              domain={[0, 64]}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={{ stroke: '#E5E7EB', length: 4 }}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              ticks={stockXTicks}
              tickFormatter={(val) => val === 0 ? 'Feb. 2026' : 'Feb. 6'}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => `${value}%`}
              domain={[-5, 3]}
              ticks={[-5, -4, -3, -2, -1, 0, 1, 2, 3]}
            />
            <ReferenceLine y={0} stroke="#1a1a1a" strokeWidth={1.5} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900 mb-2">{d.time}</p>
                      <p className="text-sm text-gray-600">
                        <span className="inline-block w-3 h-3 rounded-sm mr-1.5" style={{ backgroundColor: '#6DB9C0' }}></span>
                        <span className="font-semibold">Dow Industrials: </span>
                        {d.dow > 0 ? '+' : ''}{d.dow.toFixed(1)}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="inline-block w-3 h-3 rounded-sm mr-1.5" style={{ backgroundColor: '#2B5C5E' }}></span>
                        <span className="font-semibold">S&P 500: </span>
                        {d.sp500 > 0 ? '+' : ''}{d.sp500.toFixed(1)}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="inline-block w-3 h-3 rounded-sm mr-1.5" style={{ backgroundColor: '#B8D8DA' }}></span>
                        <span className="font-semibold">Nasdaq Composite: </span>
                        {d.nasdaq > 0 ? '+' : ''}{d.nasdaq.toFixed(1)}%
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              verticalAlign="top"
              align="right"
              height={50}
              iconType="square"
              wrapperStyle={{ paddingBottom: '10px' }}
              formatter={(value: string) => {
                const labels: { [key: string]: string } = {
                  dow: 'Dow Industrials',
                  sp500: 'S&P 500',
                  nasdaq: 'Nasdaq Composite',
                };
                return <span style={{ fontSize: '13px', color: '#374151' }}>{labels[value]}</span>;
              }}
            />
            <Line
              type="linear"
              dataKey="dow"
              stroke="#6DB9C0"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: '#6DB9C0' }}
            />
            <Line
              type="linear"
              dataKey="sp500"
              stroke="#2B5C5E"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: '#2B5C5E' }}
            />
            <Line
              type="linear"
              dataKey="nasdaq"
              stroke="#B8D8DA"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4, fill: '#B8D8DA' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-between items-center mt-4">
        <p className="text-xs text-gray-500">Source: FactSet</p>
        <p className="text-xs text-gray-500">As of Feb. 6, 4 p.m. ET</p>
      </div>
    </div>
  );
};

// Dades del gràfic de rendiment de BDCs (gen 2026 - feb 2026)
const bdcPerformanceData = [
  { date: "2 gen", Oaktree: 0, Ares: 0, Golub: 0 },
  { date: "3 gen", Oaktree: 0.2, Ares: 1.3, Golub: 0.3 },
  { date: "6 gen", Oaktree: 1.0, Ares: 2.0, Golub: 0.5 },
  { date: "7 gen", Oaktree: 0.3, Ares: 1.2, Golub: 0.2 },
  { date: "8 gen", Oaktree: -0.5, Ares: -0.3, Golub: -0.5 },
  { date: "9 gen", Oaktree: -0.7, Ares: -0.5, Golub: -0.8 },
  { date: "10 gen", Oaktree: -2.2, Ares: -1.8, Golub: -0.8 },
  { date: "13 gen", Oaktree: -0.3, Ares: 0.8, Golub: 0.5 },
  { date: "14 gen", Oaktree: 0.5, Ares: 1.0, Golub: 1.0 },
  { date: "15 gen", Oaktree: 0.8, Ares: 2.4, Golub: 1.5 },
  { date: "16 gen", Oaktree: -1.5, Ares: 1.5, Golub: 1.8 },
  { date: "17 gen", Oaktree: 0.5, Ares: 3.0, Golub: 2.0 },
  { date: "21 gen", Oaktree: 1.5, Ares: 3.5, Golub: 1.5 },
  { date: "22 gen", Oaktree: 0.8, Ares: 2.8, Golub: 1.2 },
  { date: "23 gen", Oaktree: 0.7, Ares: 3.0, Golub: 1.0 },
  { date: "24 gen", Oaktree: -0.5, Ares: 3.5, Golub: 0.8 },
  { date: "27 gen", Oaktree: 0.6, Ares: 0.8, Golub: 1.2 },
  { date: "28 gen", Oaktree: 0.0, Ares: 1.0, Golub: 0.5 },
  { date: "29 gen", Oaktree: -0.3, Ares: 0.0, Golub: 0.0 },
  { date: "30 gen", Oaktree: -0.5, Ares: -0.5, Golub: -0.3 },
  { date: "31 gen", Oaktree: -1.5, Ares: -0.5, Golub: -0.5 },
  { date: "3 feb", Oaktree: -2.5, Ares: -2.5, Golub: -2.0 },
  { date: "4 feb", Oaktree: -3.0, Ares: -3.0, Golub: -3.5 },
  { date: "5 feb", Oaktree: -5.0, Ares: -5.0, Golub: -6.0 },
  { date: "6 feb", Oaktree: -2.0, Ares: -2.0, Golub: -4.5 },
  { date: "7 feb", Oaktree: -5.5, Ares: -5.5, Golub: -9.0 },
];

// Component del gràfic de rendiment de BDCs
// Dades pels gràfics de l'article Picks & Shovels
const hyperscalerCapexData = [
  { year: "2022", capex: 140, iaCapex: 35 },
  { year: "2023", capex: 180, iaCapex: 70 },
  { year: "2024", capex: 280, iaCapex: 160 },
  { year: "2025", capex: 443, iaCapex: 310 },
  { year: "2026e", capex: 602, iaCapex: 450 },
];

const picksShovelsStocksData = [
  { name: "SanDisk", rendiment: 580, category: "Emmagatzematge" },
  { name: "Western Digital", rendiment: 210, category: "Emmagatzematge" },
  { name: "Vertiv", rendiment: 185, category: "Refrigeració" },
  { name: "Seagate", rendiment: 155, category: "Emmagatzematge" },
  { name: "Amphenol", rendiment: 72, category: "Connectors" },
  { name: "Corning", rendiment: 68, category: "Fibra" },
  { name: "GE Vernova", rendiment: 62, category: "Energia" },
  { name: "NRG Energy", rendiment: 55, category: "Energia" },
];

const infraCategoriesData = [
  { name: "Xips (GPU/TPU)", value: 45 },
  { name: "Energia i refrigeració", value: 25 },
  { name: "Emmagatzematge", value: 15 },
  { name: "Xarxes i fibra", value: 10 },
  { name: "Construcció", value: 5 },
];

const INFRA_COLORS = ["#2C5F5F", "#5BA4A4", "#9DD566", "#93C5D5", "#B0D4D4"];

const HyperscalerCapexChart = () => {
  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">L'explosió de la inversió en IA</h4>
        <p className="text-sm text-gray-600">Despesa de capital dels 5 principals hyperscalers (milers de milions $)</p>
      </div>
      <div className="h-[350px] md:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={hyperscalerCapexData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <XAxis dataKey="year" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(v) => `${v}B`} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900 mb-2">{payload[0].payload.year}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#B0D4D4' }}>Capex total: </span>${payload[0].payload.capex}B
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#2C5F5F' }}>Dedicat a IA: </span>${payload[0].payload.iaCapex}B
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
              formatter={(value) => {
                const labels: Record<string, string> = { capex: "Capex total", iaCapex: "Dedicat a IA" };
                return <span className="text-sm text-gray-700">{labels[value] || value}</span>;
              }}
            />
            <Bar dataKey="capex" fill="#B0D4D4" radius={[4, 4, 0, 0]} />
            <Bar dataKey="iaCapex" fill="#2C5F5F" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-400 mt-3">Font: CNBC, Bloomberg, presentacions de resultats de les companyies. 2026e = estimació.</p>
    </div>
  );
};

const PicksShovelsStocksChart = () => {
  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">Les accions "pics i pales" que van dominar el 2025</h4>
        <p className="text-sm text-gray-600">Rendiment borsari en % durant el 2025</p>
      </div>
      <div className="h-[350px] md:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={picksShovelsStocksData} layout="vertical" margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <XAxis type="number" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(v) => `${v}%`} />
            <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} width={100} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900">{payload[0].payload.name}</p>
                      <p className="text-sm text-gray-600">Sector: {payload[0].payload.category}</p>
                      <p className="text-sm font-semibold" style={{ color: '#2C5F5F' }}>+{payload[0].payload.rendiment}%</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="rendiment" radius={[0, 4, 4, 0]}>
              {picksShovelsStocksData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.category === "Emmagatzematge" ? "#2C5F5F" : entry.category === "Refrigeració" ? "#5BA4A4" : entry.category === "Energia" ? "#9DD566" : "#93C5D5"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-4 mt-3">
        <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#2C5F5F' }}></span>Emmagatzematge</span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#5BA4A4' }}></span>Refrigeració</span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#9DD566' }}></span>Energia</span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#93C5D5' }}></span>Connectors/Fibra</span>
      </div>
      <p className="text-xs text-gray-400 mt-3">Font: Bloomberg, S&P 500</p>
    </div>
  );
};

const InfraCategoriesChart = () => {
  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">On van a parar els diners?</h4>
        <p className="text-sm text-gray-600">Distribució estimada de la inversió en infraestructura IA</p>
      </div>
      <div className="h-[350px] md:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={infraCategoriesData}
              cx="50%"
              cy="50%"
              outerRadius={130}
              innerRadius={60}
              dataKey="value"
              label={({ name, value }) => `${name}: ${value}%`}
              labelLine={true}
            >
              {infraCategoriesData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={INFRA_COLORS[index % INFRA_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900">{payload[0].name}</p>
                      <p className="text-sm font-semibold" style={{ color: '#2C5F5F' }}>{payload[0].value}%</p>
                    </div>
                  );
                }
                return null;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-400 mt-3">Font: McKinsey, estimacions d'analistes</p>
    </div>
  );
};

// Article: Picks & Shovels IA
const PicksShovelsArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Tech / Inversió
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          Picks & Shovels: qui guanya de veritat amb la febre de l'or de la IA
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          A la febre de l'or del segle XIX, els que es van fer rics no van ser els miners, sinó els que venien pics, pales i texans. A la revolució de la IA, la història es repeteix.
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-13" className="font-medium">
            13 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>Adaptat del WSJ</span>
        </div>
      </header>

      <div className="w-full aspect-video overflow-hidden rounded-lg mb-12">
        <img
          src="/picks-shovels-ai.jpg"
          alt="Infraestructura d'IA i centres de dades"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Tothom parla de ChatGPT, de Gemini, de Claude. Però mentre el món discuteix quin model d'intel·ligència artificial és millor, els inversors més astuts miren cap a un altre lloc: <strong>les empreses que construeixen la infraestructura</strong> que fa possible tota aquesta revolució.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          L'analogia ve de la febre de l'or de Califòrnia de 1849. Dels 300.000 buscadors d'or que hi van anar, molt pocs es van fer rics. Qui sí que va fer fortuna va ser Levi Strauss, que els venia pantalons resistents, i Samuel Brannan, que els venia pics i pales. La lliçó és clara: <strong>en una febre d'or, els que subministren les eines sempre guanyen</strong>.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            "No inverteixis en l'or. Inverteix en els pics i pales." — Principi clàssic d'inversió que avui s'aplica directament a la IA.
          </p>
        </blockquote>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">La despesa més gran de la història de la tecnologia</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Per entendre la magnitud del que està passant, només cal mirar les xifres. Els cinc grans hyperscalers —Amazon (AWS), Microsoft (Azure), Google (GCP), Meta i Oracle— invertiran conjuntament uns <strong>600.000 milions de dòlars</strong> en infraestructura durant el 2026. Això és un 70% més que el 2025, que ja va ser un any de rècord.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Amazon sola preveu gastar 200.000 milions. Google entre 175.000 i 185.000 milions. Cada una d'aquestes empreses superarà individualment els 100.000 milions de dòlars en despesa de capital. I la gran majoria d'aquests diners va a parar a una cosa: <strong>centres de dades per a IA</strong>.
        </p>

        <HyperscalerCapexChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Qui són els "pics i pales" moderns?</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Si els hyperscalers són els miners, qui els ven les eines? Hi ha cinc grans categories d'empreses que es beneficien directament d'aquesta allau de diners:
        </p>

        <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mt-6 mb-3">1. Xips i semiconductors</h3>
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          <strong>Nvidia</strong> és l'exemple més pur de "pics i pales" de la IA. Cada centre de dades necessita milers de GPUs. Però no és l'única: <strong>Broadcom</strong> domina el mercat dels xips personalitzats (com els TPU de Google), <strong>SK Hynix</strong> controla el 62% del mercat de memòria d'ample de banda alt (HBM) —essencial per als GPUs— i <strong>TSMC</strong> fabrica pràcticament tots aquests xips.
        </p>

        <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mt-6 mb-3">2. Energia i refrigeració</h3>
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Un centre de dades d'IA consumeix molta més energia que un de convencional. <strong>Vertiv</strong>, que fabrica sistemes de refrigeració i gestió energètica, ha vist les seves comandes créixer un 252% interanual. <strong>Eaton</strong> subministra sistemes elèctrics, <strong>GE Vernova</strong> proporciona generació d'energia i <strong>Trane Technologies</strong> s'encarrega de la climatització especialitzada.
        </p>

        <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mt-6 mb-3">3. Emmagatzematge de dades</h3>
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Entrenar un model d'IA requereix quantitats ingents de dades. Les empreses d'emmagatzematge van ser les grans triomfadores del 2025 a l'S&P 500: <strong>SanDisk</strong> va pujar un 580%, <strong>Western Digital</strong> va ser la segona millor acció de tot l'índex, i <strong>Seagate</strong> la quarta.
        </p>

        <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mt-6 mb-3">4. Xarxes, cables i fibra</h3>
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Tots aquests centres de dades necessiten connectar-se entre ells. <strong>Amphenol</strong> (connectors d'alta velocitat), <strong>Corning</strong> (fibra òptica) i <strong>Arista Networks</strong> (commutadors de xarxa) estan entre les empreses que més es beneficien d'aquesta necessitat.
        </p>

        <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mt-6 mb-3">5. Construcció i integració</h3>
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Segons McKinsey, calen <strong>5,2 bilions de dòlars</strong> en expansió de capacitat de centres de dades fins al 2030. Empreses com <strong>Celestica</strong>, que dissenya servidors personalitzats per als hyperscalers, i les constructores especialitzades en data centers estan vivint una edat d'or.
        </p>

        <PicksShovelsStocksChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Per què aquesta estratègia funciona?</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          L'avantatge de les empreses d'infraestructura és senzill: <strong>guanyen diners independentment de quin model d'IA triomfi</strong>. Tant si el futur és de Google, d'OpenAI, d'Anthropic o d'una startup que encara no coneixem, totes necessiten GPUs, refrigeració, energia i emmagatzematge.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            No importa qui guanyi la cursa de la IA. El que importa és que tots els participants han de comprar les mateixes eines per competir.
          </p>
        </blockquote>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          A més, mentre les empreses de programari pateixen per la por que la IA les faci obsoletes (com hem vist amb el pànic als mercats privats), les empreses d'infraestructura tenen el vent a favor: <strong>com més s'accelera la cursa de la IA, més infraestructura es necessita</strong>.
        </p>

        <InfraCategoriesChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">El risc: una bombolla d'infraestructura?</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          No tot és de color de rosa. Hi ha veus que alerten d'un possible sobreexcés en la construcció de centres de dades. Si la demanda real d'IA no creix al ritme que s'espera, podríem acabar amb un excés de capacitat. El precedent de la bombolla de fibra òptica dels anys 2000, quan es va instal·lar molt més cable del necessari, és un recordatori incòmode.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però per ara, els senyals apunten en la direcció contrària. Les comandes de Nvidia estan exhaurides fins ben entrat el 2027. La capacitat de memòria HBM de SK Hynix per al 2026 ja està reservada. I els hyperscalers no donen mostres de voler alentir la despesa —al contrari, cada trimestre anuncien xifres més grans.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Conclusió: segueix els diners</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La lliçó és atemporal. Quan hi ha una febre d'or, no apostis per trobar la pepita més gran. Aposta per les empreses que venen els pics, les pales, els texans i l'aigua als qui la busquen.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Amb 600.000 milions de dòlars d'inversió prevista només el 2026, la febre de l'or de la IA no mostra signes de refredament. I les empreses d'infraestructura —els "pics i pales" moderns— són les que tenen més probabilitat de convertir aquesta febre en beneficis reals i tangibles.
        </p>
      </div>

      <footer className="mt-12 pt-8 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Adaptat d'articles del Wall Street Journal, Bloomberg i CNBC, febrer de 2026.
        </p>
      </footer>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

const BDCPerformanceChart = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">El crèdit privat, sota els focus</h4>
        <p className="text-sm text-gray-600">Rendiment borsari de vehicles de crèdit privat cotitzats</p>
      </div>
      <div className="h-[350px] md:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={bdcPerformanceData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) {
                setActiveIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <XAxis
              dataKey="date"
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              interval="preserveStartEnd"
              ticks={["2 gen", "3 feb"]}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickFormatter={(value) => `${value}%`}
              domain={[-10, 6]}
              ticks={[-10, -8, -6, -4, -2, 0, 2, 4, 6]}
            />
            <ReferenceLine y={0} stroke="#374151" strokeWidth={1.5} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <p className="text-sm font-medium text-gray-900 mb-2">{payload[0].payload.date}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#2C5F5F' }}>Oaktree Specialty Lending: </span>
                        {payload[0].payload.Oaktree > 0 ? '+' : ''}{payload[0].payload.Oaktree}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#5BA4A4' }}>Ares Capital: </span>
                        {payload[0].payload.Ares > 0 ? '+' : ''}{payload[0].payload.Ares}%
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold" style={{ color: '#B0D4D4' }}>Golub Capital BDC: </span>
                        {payload[0].payload.Golub > 0 ? '+' : ''}{payload[0].payload.Golub}%
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
              formatter={(value) => {
                const labels: Record<string, string> = {
                  Oaktree: "Oaktree Specialty Lending",
                  Ares: "Ares Capital",
                  Golub: "Golub Capital BDC",
                };
                return <span className="text-sm text-gray-700">{labels[value] || value}</span>;
              }}
            />
            <Line type="monotone" dataKey="Oaktree" stroke="#2C5F5F" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="Ares" stroke="#5BA4A4" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="Golub" stroke="#B0D4D4" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-400 mt-3">Font: FactSet</p>
    </div>
  );
};

// Article sobre el pànic de la IA als mercats privats
const PanicMercatsPrivatsArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Mercats / IA
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          El pànic de la IA als mercats privats: quan els "ingressos recurrents" deixen de ser-ho
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          Els inversors comencen a desconfiar del capital privat i dels préstecs construïts sobre resultats suposadament previsibles.
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-08" className="font-medium">
            8 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>Per Telis Demos</span>
        </div>
      </header>

      <div className="w-full aspect-video overflow-hidden rounded-lg mb-12">
        <img
          src="/private-markets-panic.jpg"
          alt="Mercats privats i IA"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La por que la intel·ligència artificial capgiri el sector del programari ha encès totes les alarmes entre els inversors de gestores d'actius privats. I la reacció ha estat fulminant: primer es ven, després es pregunta.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Les firmes d'inversió privada s'han abocat al sector tecnològic els últims anys. A finals de l'any passat, gairebé el 9% de les empreses amb capital privat al darrere operaven en l'àmbit del programari, segons dades de PitchBook. Però l'exposició és encara més gran pel costat del deute: dins l'univers de crèdit privat que segueix l'agència de qualificació KBRA, al voltant del 17% dels prestataris són empreses de programari, cosa que representa aproximadament el 22% d'un volum total de deute que supera el bilió de dòlars.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">L'atractiu dels ingressos recurrents</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El gran atractiu d'aquestes companyies per als inversors privats sempre ha estat el mateix: els ingressos recurrents per subscripció del programari al núvol. Aquest model es presta de manera natural a l'apalancament. Els inversors en deute necessiten més visibilitat a llarg termini que els d'equitat, perquè volen tenir la certesa que cobraran en els anys vinents. Els fons de préstecs, que han captat quantitats enormes de capital últimament, van àvids d'aquest tipus de paper. I com més palanqueja tingui l'empresa, més rendiment per als inversors d'equitat.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El problema és que la premissa fonamental —uns ingressos previsibles— ara trontolla. Si aquestes empreses acaben sent desplaçades per eines d'IA més barates i eficients, els seus clients podrien tenir prou incentiu per passar pel mal tràngol de cancel·lar subscripcions, o almenys exigir una rebaixa. En aquest context, l'anunci d'Anthropic aquesta setmana d'una eina d'IA per a serveis legals va caure com un llumí dins un bosc sec.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            I la cosa empitjora perquè les empreses en mans de mercats privats són per naturalesa opaques, la qual cosa alimenta el pànic encara més.
          </p>
        </blockquote>

        <BDCPerformanceChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Sessions d'explicació urgent</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Aquesta setmana, les presentacions de resultats de gestores com Blue Owl Capital i KKR, i de vehicles de crèdit cotitzats com Ares Capital, Golub Capital BDC i Oaktree Specialty Lending, s'han convertit en sessions d'explicació urgent sobre per què les seves carteres de programari estan protegides —o fins i tot ben posicionades— davant la disrupció de la IA.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Els directius han desplegat tot l'arsenal d'arguments: que fa temps que monitoritzen el risc de la IA; que les bones empreses de programari estan tan integrades en els processos dels seus clients que no es poden arrencar fàcilment; que algunes treballen amb dades propietàries que la IA no pot replicar; que les que sàpiguen operacionalitzar eines d'IA acabaran sent encara més valuoses; que les valoracions d'equitat segueixen per sobre dels nivells de quan es van concedir els préstecs, fa uns quants anys.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Les xifres, de moment, aguanten</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          En termes generals, les empreses de programari presents en carteres de crèdit privat arriben a aquest moment d'incertesa en bona forma. La mediana de creixement anualitzat de l'EBITDA entre els prestataris de programari dins l'univers que segueix KBRA ha estat del 36% durant els últims dos anys aproximadament.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però els accionistes dels vehicles de crèdit cotitzats no es donen per satisfets. L'ETF VanEck BDC Income (BIZD), que replica el comportament de les business-development companies, va esborrar dijous el repunt de dimecres i acumula una caiguda de més del 5% en la setmana. Les grans gestores d'actius alternatius també han reprès les baixades pronunciades: Blackstone, KKR i Apollo Global Management van caure totes més d'un 5% dijous.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Sense garanties concretes</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          A curt termini, serà difícil que els inversors obtinguin gaires garanties concretes i numèriques. D'una banda, els mercats de crèdit públic no ajuden com a referència: el volum de préstecs apalancats a empreses de programari en situació de dificultat es va duplicar al gener, segons PitchBook LCD. I els casos recents de préstecs privats valorats a cost que poc després van patir rebaixes dràstiques no fan més que alimentar la desconfiança.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">La diferència entre equitat i deute</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Cal dir que hi ha una diferència important entre tenir l'equitat d'una empresa i ser-ne creditor. Una companyia que vegi les seves perspectives de creixement retallades pot patir un enfonsament de la seva valoració bursàtil, però seguir cobrant els xecs recurrents dels clients existents, cosa que la fa un crèdit més segur. En teoria, una empresa de programari empresarial amb ingressos per subscripció encaixa perfectament en aquest model.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però l'estat d'ànim actual dels inversors és presumir que els crèdits de programari poden estar deteriorats fins que el rendiment real demostri el contrari.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            A més, algunes gestores han ampliat els últims anys el seu crèdit a empreses de programari que encara no són rendibles. Els anomenats préstecs sobre ingressos recurrents es basen en projeccions de facturació futura, no en beneficis reals.
          </p>
        </blockquote>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La idea és que en algun moment es converteixin en préstecs basats en resultats. "Més competència, un producte més vulnerable… tot això pot afectar la trajectòria de quan l'empresa arribarà a ser rendible", apunta Alen Lin, director sènior de recerca de crèdit tecnològic nord-americà de Fitch.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Evidentment, les gestores i els fons que demostrin la solidesa de les seves carteres al llarg del cicle acabaran captant una quota més gran de fluxos i actius en el futur. Però ara mateix, molts inversors no semblen disposats a quedar-se per veure qui serà.
        </p>
      </div>

      <footer className="mt-12 pt-8 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Adaptat de l'article original de Telis Demos al Wall Street Journal, 7 de febrer de 2026.
        </p>
      </footer>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

// Article sobre la recuperació de la borsa i la IA
const RecuperacioBorsaArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Mercats / IA
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          La recuperació de la borsa i la pregunta sobre la IA que ningú vol respondre
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          La borsa torna a pujar, però la mentalitat de "comprar la baixada" amaga una pregunta incòmoda: pot una economia créixer sense el mercat laboral?
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-07" className="font-medium">
            7 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>Per Kyla Scanlon</span>
        </div>
      </header>

      <a
        href="https://youtu.be/RIMBPaj9guE"
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full aspect-video overflow-hidden rounded-lg mb-12 bg-[#FF0000] relative group cursor-pointer"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-14 md:w-24 md:h-16 bg-white/90 group-hover:bg-white rounded-xl flex items-center justify-center transition-all duration-150 shadow-lg group-hover:scale-105">
            <svg viewBox="0 0 24 24" className="w-8 h-8 md:w-10 md:h-10 text-[#FF0000] ml-1" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </a>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La borsa ha tornat a pujar. I amb ella, la mentalitat de "comprar la baixada" que s'ha arrelat profundament entre els inversors minoristes. La premissa és senzilla: es pressuposa que la borsa nord-americana sempre pujarà, i teòricament hauria de ser així si s'espera que l'economia continuï creixent i les empreses continuïn tenint bons resultats.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però la IA ho complica tot. El motiu citat per la pujada de la borsa és que l'economia sembla resilient i que hi ha resultats empresarials saludables. Tanmateix, si mirem el mercat laboral, el que veiem no és precisament una economia resilient. És una economia que comença a tenir problemes seriosos.
        </p>

        <StockIndexWeekChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Una economia que ja no necessita treballadors?</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El que és important entendre sobre l'economia cap a la qual anem és que ja no depèn del mercat laboral com abans. Si tot aquest tema de la IA funciona, podríem viure en un món on l'economia creix al 4%, la productivitat augmenta, però la força laboral s'asseca.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La inflació baixaria perquè la IA faria tot una mica més barat. Però aquesta és la conversa estranya sobre una "economia resilient": ja no requereix un mercat laboral fort gràcies a la tecnologia.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            Si la IA compleix les seves promeses, aquesta serà una economia que creix a través de la intensitat de capital en lloc de la participació laboral. Això va més enllà d'una recessió. Es tracta del contracte social.
          </p>
        </blockquote>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">El cicle de la borsa amb la IA</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La borsa està en un cicle ara mateix on intenta esbrinar exactament què significarà la IA. Sembla que de sobte totes les empreses reaccionen igual: "Oh, si Anthropic pot fer un assistent legal realment bo amb IA, pot substituir-nos a tots a tot arreu." Tothom s'espanta. La borsa cau. La gent torna a comprar. I el cicle es repeteix una vegada i una altra.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però el que és estrany d'aquest cicle en particular és la quantitat de diners en joc. S'estan gastant quantitats enormes en tot això i ningú sap qui se'n beneficia realment.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Qui guanya i qui perd?</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Teòricament, al principi els laboratoris d'IA se'n beneficiarien. Guanyen. Es fan molt rics. Les corporacions compren els seus productes. Els marges exploten. Tothom va bé a nivell corporatiu.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però després ningú pot permetre's comprar el que les corporacions venen. Si el mercat laboral realment té problemes, la despesa dels consumidors representa el 70% de l'economia. Aquesta és la qüestió amb la qual molta gent lluita ara: què significa tenir una economia que creix sense que el mercat laboral sigui estable?
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Perspectives per a l'any vinent</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Per a l'any vinent, és improbable que entrem en algun tipus de recessió perquè el govern està gastant quantitats boges de diners, i aquesta despesa proporcionarà un terra a l'economia. Però si totes les promeses —i són només promeses— es compleixen, això podria ser una economia molt diferent a la que la gent està acostumada.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">La pregunta fonamental</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Si el que els laboratoris d'IA prometen funciona, aquesta serà una economia que creix a través de la intensitat de capital en lloc de la participació laboral. Això va més enllà d'una recessió. Es tracta del contracte social.
        </p>

        <AILayoffsChart />

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            L'economia, en última instància, és sobre les persones. El que estem preguntant amb la IA és: pot una economia funcionar sense persones? Jo crec que no. Però aquesta sembla ser l'aposta de bilions de dòlars.
          </p>
        </blockquote>
      </div>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

// Article sobre Google/Alphabet i IA
const GoogleAlphabetArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Tech / IA
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          Google aposta fort pel seu lideratge en IA
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          El creixement accelerat en publicitat i núvol justifica la pujada recent de les accions d'Alphabet, però la inversió anunciada en infraestructura encara deixa tothom bocabadat.
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-05" className="font-medium">
            5 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>Per Dan Gallagher</span>
        </div>
      </header>

      <div className="w-full aspect-video overflow-hidden rounded-lg mb-12">
        <img
          src="/google-ai.jpg"
          alt="Sundar Pichai, CEO de Google"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El lema de la cursa de la intel·ligència artificial avui hauria de ser: si tens recursos, gasta'ls.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Aquest és el missatge que Meta Platforms va fer seu la setmana passada amb els resultats del quart trimestre, quan la companyia mare de Facebook i Instagram va anunciar plans per gastar fins a 135.000 milions de dòlars en inversió de capital aquest any, comparats amb els 72.000 milions de l'any anterior. Google ha anat més lluny aquest dimecres amb el seu propi pla d'invertir fins a 185.000 milions de dòlars aquest any, pràcticament el doble del desemborsament de l'any passat.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Els ingressos anuals de Google ja superen els 400.000 milions de dòlars, aproximadament el doble que els de Meta. Tot i així, aquesta nova xifra d'inversió, fins i tot per a una empresa que últimament va com un tret, deixa tothom sense paraules. El preu de les accions d'Alphabet, la companyia mare de Google, va baixar lleugerament en les operacions fora d'horari aquest dimecres després de la presentació de resultats del quart trimestre i la conferència amb analistes.
        </p>

        <CapitalExpendituresChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Capital polític i financer</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Google té tant el capital polític com el financer per fer una aposta d'aquesta magnitud. El seu model Gemini 3 l'ha situat al capdamunt del rendiment entre els models d'IA, mentre que la distribució inigualable del seu cercador i productes com Gmail han impulsat ràpidament l'adopció. Google va anunciar dimecres que té més de 750 milions d'usuaris actius mensuals només a la seva aplicació Gemini, que representa només una part dels usuaris reals de Gemini.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La bona acollida de Gemini, juntament amb la victòria de Google davant els esforços del govern federal per desmantellar l'empresa, han animat els inversors en un moment en què el sentiment sobre tecnologia i IA es mostra vacil·lant. El valor de les accions d'Alphabet ha pujat al voltant d'un 20% en els darrers tres mesos. Nvidia, Microsoft, Amazon i Broadcom han perdut terreny durant aquest mateix període.
        </p>

        <StockPerformanceChart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Una vaca lletrera lucrativa</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però el capital real també compta. I aquí és on el model de negoci de Google treu més profit. El braç publicitari de l'empresa ha estat durant molt de temps una vaca lletrera lucrativa que encara aconsegueix taxes de creixement de dos dígits. De fet, aquest creixement s'ha accelerat: els ingressos per publicitat van créixer un 14% al quart trimestre, comparat amb el 13% del trimestre anterior.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La divisió de computació al núvol va ser encara més impressionant, amb uns ingressos que van saltar un 48% interanual fins als 17.700 milions de dòlars. Google Cloud no havia vist un creixement així des de principis del 2021, quan el negoci era menys d'un terç de la mida actual.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            Google Cloud va registrar un benefici operatiu rècord de 5.300 milions de dòlars en l'últim trimestre, una xifra que va superar en un 45% els objectius de Wall Street.
          </p>
        </blockquote>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Els negocis florent de l'empresa van generar gairebé 165.000 milions de dòlars en flux de caixa operatiu el 2025, la xifra més alta de l'S&P 500, segons dades de S&P Global Market Intelligence.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Una aposta considerable</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Aquests resultats sòlids ajudaran els inversors a digerir el darrer pla d'inversió. Però gastar el que podria ser el 40% dels ingressos anuals en xips d'IA i infraestructura relacionada segueix sent una aposta considerable. Aquestes inversions elevaran bruscament els càrrecs per depreciació, que en l'últim trimestre van reduir els beneficis nets en un 18%.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          I Google ja no és la ganga que era fa menys d'un any, quan les pors de desmantellament tenien l'acció cotitzant a menys de 16 vegades els beneficis projectats. Aquest múltiple gairebé s'ha duplicat des d'aleshores.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          I això passa mentre el mercat mostra més preocupació sobre com la IA pertorbarà els grans actors tecnològics establerts, incloent-hi molts proveïdors de programari al núvol que són clients importants de Google, Microsoft i Amazon. El propi negoci de Google funciona molt bé. Però la natura interconnectada d'aquest negoci significa que no pot estar sol del tot.
        </p>
      </div>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

// === SENIORS ECONOMIA ARTICLE CHARTS ===

const seniorsShare70Data = [
  { any: '1989', populacio: 8, patrimoni: 19 },
  { any: '1995', populacio: 8.8, patrimoni: 20.5 },
  { any: '2000', populacio: 8.6, patrimoni: 22 },
  { any: '2005', populacio: 8.6, patrimoni: 24 },
  { any: '2010', populacio: 9.0, patrimoni: 26 },
  { any: '2015', populacio: 9.9, patrimoni: 28 },
  { any: '2020', populacio: 10.8, patrimoni: 30 },
  { any: '2024/25', populacio: 12, patrimoni: 32 },
];

const elderlyPopData = [
  { any: '1981', mes65: 11.3, mes70: 7.2 },
  { any: '1985', mes65: 11.7, mes70: 7.8 },
  { any: '1990', mes65: 12.3, mes70: 8.5 },
  { any: '1995', mes65: 12.5, mes70: 8.8 },
  { any: '2000', mes65: 12.3, mes70: 8.6 },
  { any: '2005', mes65: 12.2, mes70: 8.6 },
  { any: '2010', mes65: 13.0, mes70: 8.9 },
  { any: '2015', mes65: 14.9, mes70: 9.8 },
  { any: '2020', mes65: 16.2, mes70: 10.8 },
  { any: '2024', mes65: 17.5, mes70: 12.0 },
];

const wealthByAgeNWData = [
  { grup: '<40 anys', a1989: 12, a2025: 7 },
  { grup: '40-54 anys', a1989: 32, a2025: 20 },
  { grup: '55-69 anys', a1989: 37, a2025: 42 },
  { grup: '70+ anys', a1989: 19, a2025: 32 },
];

const wealthByAgeEqData = [
  { grup: '<40 anys', a1989: 11, a2025: 6 },
  { grup: '40-54 anys', a1989: 31, a2025: 15 },
  { grup: '55-69 anys', a1989: 36, a2025: 40 },
  { grup: '70+ anys', a1989: 23, a2025: 39 },
];

const laborForceSeniorsData = [
  { periode: '2015', jovenilAdult: -1.5, seniors: -1.5 },
  { periode: '2016', jovenilAdult: -1.3, seniors: -1.3 },
  { periode: '2017', jovenilAdult: -1.0, seniors: -1.0 },
  { periode: '2018', jovenilAdult: -0.8, seniors: -0.9 },
  { periode: '2019', jovenilAdult: -0.4, seniors: -0.7 },
  { periode: 'gen 2020', jovenilAdult: 0, seniors: 0 },
  { periode: 'abr 2020', jovenilAdult: -1.7, seniors: -1.2 },
  { periode: '2021', jovenilAdult: -0.6, seniors: -1.1 },
  { periode: '2022', jovenilAdult: -0.1, seniors: -1.0 },
  { periode: '2023', jovenilAdult: 0.4, seniors: -0.9 },
  { periode: '2024', jovenilAdult: 0.7, seniors: -0.7 },
  { periode: '2025', jovenilAdult: 0.8, seniors: -1.2 },
];

const elderlySpendingGDPData = [
  { any: '1990', actual: 5.9, projectat: null },
  { any: '1993', actual: 5.7, projectat: null },
  { any: '1996', actual: 6.5, projectat: null },
  { any: '1999', actual: 6.4, projectat: null },
  { any: '2002', actual: 6.6, projectat: null },
  { any: '2005', actual: 6.7, projectat: null },
  { any: '2008', actual: 7.9, projectat: null },
  { any: '2011', actual: 8.0, projectat: null },
  { any: '2014', actual: 8.2, projectat: null },
  { any: '2017', actual: 8.5, projectat: null },
  { any: '2020', actual: 9.0, projectat: null },
  { any: '2021', actual: 7.8, projectat: null },
  { any: '2024', actual: 9.4, projectat: 9.4 },
  { any: '2028', actual: null, projectat: 10.5 },
  { any: '2032', actual: null, projectat: 11.0 },
  { any: '2035', actual: null, projectat: 11.3 },
];

const SeniorsShare70Chart = () => (
  <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
    <div className="mb-4">
      <h4 className="text-lg font-semibold text-gray-900">La quota de la gent de 70+ anys</h4>
      <p className="text-sm text-gray-600">Percentatge del total de la població i del patrimoni net dels EUA</p>
    </div>
    <div className="h-[300px] md:h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={seniorsShare70Data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <XAxis dataKey="any" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(v) => `${v}%`} domain={[0, 40]} />
          <Tooltip formatter={(v: number, n: string) => [`${v}%`, n === 'populacio' ? 'Quota de població (70+)' : 'Quota de patrimoni net (70+']} />
          <Legend formatter={(v: string) => v === 'populacio' ? 'Quota de població (70+)' : 'Quota de patrimoni net (70+)'} />
          <Line type="monotone" dataKey="populacio" stroke="#2C5F5F" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="patrimoni" stroke="#9DD566" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <p className="text-xs text-gray-400 mt-3">Font: Reserva Federal</p>
  </div>
);

const ElderlyPopulationGrowthChart = () => (
  <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
    <div className="mb-4">
      <h4 className="text-lg font-semibold text-gray-900">Més Amèrica és anciana</h4>
      <p className="text-sm text-gray-600">Quota de la població dels EUA per franja d'edat (%)</p>
    </div>
    <div className="h-[300px] md:h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={elderlyPopData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <XAxis dataKey="any" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(v) => `${v}%`} domain={[0, 20]} />
          <Tooltip formatter={(v: number, n: string) => [`${v}%`, n === 'mes65' ? '65 anys i més' : '70 anys i més']} />
          <Legend formatter={(v: string) => v === 'mes65' ? '65 anys i més' : '70 anys i més'} />
          <Line type="monotone" dataKey="mes65" stroke="#5BA4A4" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="mes70" stroke="#2C5F5F" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <p className="text-xs text-gray-400 mt-3">Font: Census Bureau via Haver</p>
  </div>
);

const WealthByAgeChart = () => (
  <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
    <div className="mb-4">
      <h4 className="text-lg font-semibold text-gray-900">Els ancians controlen més riquesa de les llars</h4>
      <p className="text-sm text-gray-600">Quota del total de riquesa per grup d'edat (%). 3T 1989 vs. 3T 2025</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <p className="text-sm font-medium text-gray-700 text-center mb-2">Patrimoni net</p>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={wealthByAgeNWData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="grup" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 10, fill: '#6B7280' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: number, n: string) => [`${v}%`, n === 'a1989' ? '3T 1989' : '3T 2025']} />
              <Legend formatter={(v: string) => v === 'a1989' ? '3T 1989' : '3T 2025'} />
              <Bar dataKey="a1989" fill="#B0D4D4" name="a1989" radius={[3, 3, 0, 0]} />
              <Bar dataKey="a2025" fill="#2C5F5F" name="a2025" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700 text-center mb-2">Accions i fons d'inversió</p>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={wealthByAgeEqData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="grup" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 10, fill: '#6B7280' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: number, n: string) => [`${v}%`, n === 'a1989' ? '3T 1989' : '3T 2025']} />
              <Bar dataKey="a1989" fill="#B0D4D4" name="a1989" radius={[3, 3, 0, 0]} />
              <Bar dataKey="a2025" fill="#2C5F5F" name="a2025" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
    <p className="text-xs text-gray-400 mt-3">Font: Reserva Federal. Nota: Els números poden no sumar 100% per arrodoniment.</p>
  </div>
);

const LaborForceParticipationChart = () => (
  <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
    <div className="mb-4">
      <h4 className="text-lg font-semibold text-gray-900">Treballant menys a partir dels 65</h4>
      <p className="text-sm text-gray-600">Canvi en la taxa d'activitat laboral des del gener de 2020 (punts percentuals, mitjana de 12 mesos)</p>
    </div>
    <div className="h-[300px] md:h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={laborForceSeniorsData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <XAxis dataKey="periode" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 10, fill: '#6B7280' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(v) => `${v}`} domain={[-2, 1.5]} />
          <Tooltip formatter={(v: number, n: string) => [`${v} pp`, n === 'jovenilAdult' ? '16-64 anys' : '65 anys i més']} />
          <Legend formatter={(v: string) => v === 'jovenilAdult' ? '16-64 anys' : '65 anys i més'} />
          <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="3 3" />
          <Line type="monotone" dataKey="jovenilAdult" stroke="#5BA4A4" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="seniors" stroke="#2C5F5F" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <p className="text-xs text-gray-400 mt-3">Font: Departament de Treball via Haver</p>
  </div>
);

const ElderlySpendingGDPChart = () => (
  <div className="chart-container my-8 bg-white rounded-lg border border-gray-200 p-4 md:p-6">
    <div className="mb-4">
      <h4 className="text-lg font-semibold text-gray-900">Els ancians costen més</h4>
      <p className="text-sm text-gray-600">Despesa federal en programes per a la gent gran, % del PIB</p>
    </div>
    <div className="h-[300px] md:h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={elderlySpendingGDPData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <XAxis dataKey="any" axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(v) => `${v}%`} domain={[4, 13]} />
          <Tooltip formatter={(v: number | null, n: string) => v !== null ? [`${v}%`, n === 'actual' ? 'Real' : 'Projectat (CBO)'] : ['-', '']} />
          <Legend formatter={(v: string) => v === 'actual' ? 'Real' : 'Projectat (CBO)'} />
          <Line type="monotone" dataKey="actual" stroke="#5BA4A4" strokeWidth={2} dot={false} connectNulls={false} />
          <Line type="monotone" dataKey="projectat" stroke="#5BA4A4" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <p className="text-xs text-gray-400 mt-3">Font: Congressional Budget Office. Nota: Inclou Seguretat Social, Medicare, jubilació civil i militar, i beneficis de veterans.</p>
  </div>
);

const SeniorsEconomiaArticle = ({ onBack }: { onBack: () => void }) => {
  const articleRef = useRef<HTMLDivElement>(null);
  return (
    <article ref={articleRef} className="w-full max-w-3xl mx-auto px-6 lg:px-12 py-8 lg:py-16 animate-fade-in-up">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12 transition-colors duration-150 ease"
      >
        <ArrowLeft className="w-4 h-4" /> Tornar al blog
      </button>

      <header className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider bg-[#9DD566]/10 px-3 py-1 rounded-full">
            Economia / Política
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          Més de 65 anys? Felicitats: teniu l'economia
        </h1>

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
          Els ancians estan físicament i financerament més sans que mai. Aleshores, per què les seves necessitats continuen tenint prioritat sobre les de les generacions joves?
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <time dateTime="2026-02-19" className="font-medium">
            19 de febrer de 2026
          </time>
          <span className="text-gray-300">•</span>
          <span>Greg Ip, WSJ</span>
        </div>
      </header>

      <div className="w-full aspect-video overflow-hidden rounded-lg mb-12 bg-gray-50 flex items-center justify-center">
        <span className="text-5xl">📈</span>
      </div>

      <div className="prose prose-lg lg:prose-xl max-w-none">
        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La demografia, l'augment dels beneficis corporatius i la disparada dels preus dels actius han produït conjuntament una transformació silenciosa a l'economia americana. Una gran part d'aquesta economia ara és a les mans dels ancians.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Al tercer trimestre de l'any passat, les persones de 70 anys o més controlaven aproximadament el <strong>39% de totes les accions i fons d'inversió de les llars</strong>, en comparació amb el 22% el 2007, segons dades de la Reserva Federal. La seva quota de patrimoni net —actius menys deutes— era del <strong>32%, enfront del 20% de dues dècades enrere</strong>.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Aquesta és una bona notícia: mai no havia estat millor moment per ser gran als Estats Units. Però també posa en evidència les nostres prioritats nacionals desajustades. Continuem abocant recursos per fer que els ancians estiguin còmodes i feliços quan els punts de pressió de l'economia es troben en un altre lloc.
        </p>

        <SeniorsShare70Chart />

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">Com ha crescut la riquesa dels jubilats</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          La riquesa s'acumula amb l'edat, de manera que les persones a la jubilació tendeixen a tenir molt més que les generacions joves. Des de 1989, l'apreciació dels actius ha beneficiat tots els estalviadors, però especialment els que van comprar les seves primeres cases i accions als anys vuitanta, que es beneficien del llarg desplaçament de la renda econòmica del treball al capital.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El patrimoni net mitjà d'una persona d'entre 65 i 74 anys va ser d'<strong>1,8 milions de dòlars el 2022</strong>, un 178% més que el del mateix grup d'edat el 1989, ajustat per inflació.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El que fa que la riquesa dels jubilats actuals sigui veritablement formidable és el seu nombre. Des que els primers baby boomers van qualificar per a la Seguretat Social el 2008, la quota de la població dels EUA de 65 anys o més ha pujat del <strong>13% al 18%</strong>.
        </p>

        <ElderlyPopulationGrowthChart />

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Les mitjanes queden esbiaixades per gent com Warren Buffett i els seus companys milionaris. El <strong>patrimoni medià</strong> d'una persona d'entre 65 i 74 anys el 2022 era de <strong>410.000 dòlars</strong>, menys d'un quart de la mitjana. I moltes persones arriben als 65 amb pocs o cap actiu financer.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Però fins i tot ells tenen riquesa, mesurada holísticament. Si es tradueixen els beneficis de la Seguretat Social en termes de valor present, el jubilat mitjà als 65 anys tenia una anualitat valorada en <strong>305.000 dòlars el 2019</strong>, un 160% més que el 1989, ajustat per inflació.
        </p>

        <WealthByAgeChart />

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Gràcies a programes com la Seguretat Social i Medicare, la pobresa entre la gent gran és molt inferior a la de la resta de la població. Gairebé el <strong>80% de les persones de 65 anys o més</strong> posseeixen la seva casa, segons el Census Bureau, una taxa que s'ha mantingut aproximadament estable durant dues dècades malgrat que la propietat de l'habitatge ha caigut per als grups de 35 a 64 anys.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El jubilat mitjà als 65 anys també és més sa que el seu homòleg d'una generació enrere. L'esperança de vida als 65 anys va assolir un nou rècord de <strong>19,7 anys el 2024</strong>. La incidència de la demència entre la gent gran ha caigut notablement en les darreres dècades.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            En un país de pessimistes, la gent gran és la que té una visió optimista. I amb raó: mai havia estat tan bo ser gran als Estats Units.
          </p>
        </blockquote>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">L'empremta grisa al mercat laboral</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          A mesura que creix la quota de la gent gran en la població i la riquesa, les seves prioritats i preferències modelen l'economia. Representen una proporció creixent de la despesa de consum. L'atenció sanitària ha representat <strong>tot el creixement net d'ocupació dels darrers 12 mesos</strong>, reflectint les necessitats d'una societat envellida.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El problema és que, mentre la riquesa dels jubilats pot clarament finançar molt de consum, els treballadors han de produir el que els jubilats consumeixen. I en relació amb els jubilats, el nombre de treballadors s'està reduint.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El 1983, el Congrés va modificar la Seguretat Social per elevar gradualment l'edat de jubilació plena de 65 a 67 anys. La quota de persones de 65 anys o més que participaven en la força laboral va anar augmentant fins al 2020. Amb l'esclat de la Covid-19, la participació laboral va caure en picat per a tots els grups d'edat. Aviat es va recuperar per als menors de 65, però no per als de 65 i més.
        </p>

        <LaborForceParticipationChart />

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Aquesta és una de les raons per les quals una proporció menor de la població treballa avui que el 2019. La caiguda de la immigració intensificarà la tendència, ja que els immigrants tenen més probabilitats de treballar que els nadius.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">La desigualtat intergeneracional</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          L'any passat, el senador republicà Ted Cruz va proposar donar a cada nounat una participació en el capitalisme amb un compte d'accions de 1.000 dòlars finançat federalment. La proposta va entrar a la «megallei» republicana com a «Comptes Trump». És un pas cap a abordar el desequilibri de riquesa entre joves i vells. Però és un pas molt petit.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Per comparació: el 2019 el govern federal va gastar més de <strong>29.000 dòlars per persona de 65 anys o més</strong> a través de la Seguretat Social, Medicare i prestacions de jubilació civils i militars, <strong>cinc vegades el que va gastar per cada nen menor de 18 anys</strong>. El 2023, la despesa per cada persona gran havia augmentat un <strong>19%</strong> en termes reals; per a cada nen, tan sols un <strong>2%</strong>.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Tots dos partits continuen enriquint les prestacions per als americans de més edat. El 2022, es van ampliar els beneficis dels veterans amb un cost de 277.000 milions de dòlars en deu anys. El 2024, es van estendre els beneficis de la Seguretat Social a alguns empleats municipals amb un preu de gairebé 200.000 milions de dòlars en deu anys. A petició del president Trump, la «megallei» de l'any passat va incloure una deducció fiscal de fins a 6.000 dòlars per a les persones de 65 anys o més.
        </p>

        <ElderlySpendingGDPChart />

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          El cost s'està engolint el pressupost. La despesa federal en programes per a la gent gran ha pujat del <strong>6,9% del PIB el 2007 al 9,4% l'any passat</strong>, i arribarà a l'<strong>11,3% en una dècada</strong>, projecta l'Oficina Pressupostària del Congrés.
        </p>

        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mt-8 mb-4">On es troba el veritable conflicte generacional</h2>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Els ancians en gran mesura estan fora del mercat laboral i, per tant, no han de preocupar-se per ser substituïts per la intel·ligència artificial. La majoria posseeixen les seves cases, sovint sense deutes. Tothom es preocupa pels costos sanitaris, però els ancians compten amb Medicare finançat públicament. Res d'això és cert per a les generacions joves.
        </p>

        <p className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6">
          Els programes per a la gent gran, juntament amb els interessos del deute, són el principal motor del dèficit pressupostari. El 2032, la Seguretat Social ja no podrà pagar prestacions completes. Solucionar el dèficit i la Seguretat Social requerirà alguna combinació d'impostos més alts o prestacions futures més baixes, i tots dos afectaran sobretot els que ara no estan en edat de treball.
        </p>

        <blockquote className="border-2 border-[#9DD566] p-4 my-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 italic">
            "Si creus que els joves estan angoixats ara, espera't." — Greg Ip, Wall Street Journal
          </p>
        </blockquote>
      </div>

      <footer className="mt-12 pt-8 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Adaptat de l'article de Greg Ip al Wall Street Journal, 19 de febrer de 2026.
        </p>
      </footer>

      <AskAIButtons contentRef={articleRef} />
    </article>
  );
};

const BlogSection = () => {
  const [selectedArticle, setSelectedArticle] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Update meta tags for social sharing
  const updateMetaTags = (post: typeof blogPosts[0] | null) => {
    const baseUrl = "https://2147.cat";

    if (post) {
      // Article-specific meta tags
      document.title = `${post.title} - 2147`;

      // Update or create meta tags
      const updateMetaTag = (property: string, content: string) => {
        let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute("property", property);
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", content);
      };

      const updateMetaName = (name: string, content: string) => {
        let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute("name", name);
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", content);
      };

      // Open Graph tags
      updateMetaTag("og:title", post.title);
      updateMetaTag("og:description", post.description || post.excerpt);
      updateMetaTag("og:image", `${baseUrl}${post.image}`);
      updateMetaTag("og:url", `${baseUrl}/blog/${post.slug}`);
      updateMetaTag("og:type", "article");

      // Twitter tags
      updateMetaTag("twitter:title", post.title);
      updateMetaTag("twitter:description", post.description || post.excerpt);
      updateMetaTag("twitter:image", `${baseUrl}${post.image}`);
      updateMetaTag("twitter:card", "summary_large_image");

      // Description meta tag
      updateMetaName("description", post.description || post.excerpt);
    } else {
      // Blog list meta tags (default)
      document.title = "Blog | 2147";

      const updateMetaTag = (property: string, content: string) => {
        const meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
        if (meta) meta.setAttribute("content", content);
      };

      const updateMetaName = (name: string, content: string) => {
        const meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (meta) meta.setAttribute("content", content);
      };

      const blogDescription = "Portfolios 13F, assistent financer i contingut explicatiu en un sol lloc. Dades ordenades i explicacions clares per entendre millor empreses i mercats.";
      updateMetaTag("og:title", "Blog | 2147");
      updateMetaTag("og:description", blogDescription);
      updateMetaTag("og:image", `${baseUrl}/preview.png`);
      updateMetaTag("og:url", `${baseUrl}/blog`);

      updateMetaTag("twitter:title", "Blog | 2147");
      updateMetaTag("twitter:description", blogDescription);
      updateMetaTag("twitter:image", `${baseUrl}/preview.png`);

      updateMetaName("description", blogDescription);
    }
  };

  // Check URL on mount and handle browser back/forward
  useEffect(() => {
    const updateArticleFromUrl = () => {
      const path = window.location.pathname;

      // Decode URI components to handle special characters
      const decodedPath = decodeURIComponent(path);

      const match = decodedPath.match(/^\/blog\/(.+)$/);
      if (match) {
        const slug = match[1];
        const post = blogPosts.find(p => p.slug === slug);
        if (post) {
          console.log('[BlogSection] Found article:', post.id, 'for slug:', slug);
          setSelectedArticle(post.id);
          updateMetaTags(post);
        } else {
          console.warn('[BlogSection] Article not found for slug:', slug);
          setSelectedArticle(null);
          updateMetaTags(null);
        }
      } else if (path === "/blog" || path === "/blog/") {
        console.log('[BlogSection] Showing blog list');
        setSelectedArticle(null);
        updateMetaTags(null);
      }

      // Done initializing
      setIsInitializing(false);
    };

    // Check URL on mount
    updateArticleFromUrl();

    // Listen for browser back/forward navigation
    const handlePopState = () => {
      updateArticleFromUrl();
      window.scrollTo(0, 0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleArticleClick = (post: typeof blogPosts[0]) => {
    setSelectedArticle(post.id);
    window.history.pushState({}, "", `/blog/${post.slug}`);
    window.scrollTo(0, 0);
  };

  const handleBackClick = () => {
    setSelectedArticle(null);
    window.history.pushState({}, "", "/blog");
    window.scrollTo(0, 0);
  };

  // Show nothing while initializing to prevent flash
  if (isInitializing) {
    return null;
  }

  if (selectedArticle === "ia-programari-creixement") {
    return <IAProgramariArticle onBack={handleBackClick} />;
  }

  if (selectedArticle === "google-alphabet-ia") {
    return <GoogleAlphabetArticle onBack={handleBackClick} />;
  }

  if (selectedArticle === "warsh-fed") {
    return <WarshArticle onBack={handleBackClick} />;
  }

  if (selectedArticle === "oracle-openai") {
    return <OracleArticle onBack={handleBackClick} />;
  }

  if (selectedArticle === "picks-shovels-ia") {
    return <PicksShovelsArticle onBack={handleBackClick} />;
  }

  if (selectedArticle === "panic-mercats-privats-ia") {
    return <PanicMercatsPrivatsArticle onBack={handleBackClick} />;
  }

  if (selectedArticle === "recuperacio-borsa-ia") {
    return <RecuperacioBorsaArticle onBack={handleBackClick} />;
  }

  if (selectedArticle === "seniors-propietaris-economia") {
    return <SeniorsEconomiaArticle onBack={handleBackClick} />;
  }

  return (
    <section className="w-full max-w-5xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      <div className="prose prose-lg max-w-none mb-16">
        <h2 className="text-3xl font-medium text-gray-900 mb-4">Blog</h2>

        <p className="text-gray-700 leading-relaxed">
          Anàlisis de mercats, empreses i decisions d'inversió.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {[...blogPosts].sort((a, b) => {
          // Parse dates in format "5 feb 2026"
          const parseDate = (dateStr: string) => {
            const months: Record<string, number> = {
              gen: 0, feb: 1, mar: 2, abr: 3, mai: 4, jun: 5,
              jul: 6, ago: 7, set: 8, oct: 9, nov: 10, des: 11
            };
            const [day, month, year] = dateStr.split(' ');
            return new Date(parseInt(year), months[month.toLowerCase()], parseInt(day));
          };
          
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
        }).map((post, index) => (
          <article
            key={index}
            className={`bg-white border rounded-lg overflow-hidden transition-all duration-150 ease flex flex-col relative ${
              (post.id === "ia-programari-creixement" || post.id === "google-alphabet-ia" || post.id === "warsh-fed" || post.id === "oracle-openai" || post.id === "panic-mercats-privats-ia" || post.id === "recuperacio-borsa-ia" || post.id === "picks-shovels-ia" || post.id === "seniors-propietaris-economia")
                ? "border-[#9DD566] hover:border-[#8bc455] hover:shadow-md cursor-pointer"
                : "border-gray-200 hover:border-gray-300"
            }`}
            onClick={() => (post.id === "ia-programari-creixement" || post.id === "google-alphabet-ia" || post.id === "warsh-fed" || post.id === "oracle-openai" || post.id === "panic-mercats-privats-ia" || post.id === "recuperacio-borsa-ia" || post.id === "picks-shovels-ia" || post.id === "seniors-propietaris-economia") && handleArticleClick(post)}
            role={(post.id === "ia-programari-creixement" || post.id === "google-alphabet-ia" || post.id === "warsh-fed" || post.id === "oracle-openai" || post.id === "panic-mercats-privats-ia" || post.id === "recuperacio-borsa-ia" || post.id === "picks-shovels-ia" || post.id === "seniors-propietaris-economia") ? "button" : undefined}
            tabIndex={(post.id === "ia-programari-creixement" || post.id === "google-alphabet-ia" || post.id === "warsh-fed" || post.id === "oracle-openai" || post.id === "panic-mercats-privats-ia" || post.id === "recuperacio-borsa-ia" || post.id === "picks-shovels-ia" || post.id === "seniors-propietaris-economia") ? 0 : undefined}
            onKeyDown={(e) => {
              if ((post.id === "ia-programari-creixement" || post.id === "google-alphabet-ia" || post.id === "warsh-fed" || post.id === "oracle-openai" || post.id === "panic-mercats-privats-ia" || post.id === "recuperacio-borsa-ia" || post.id === "picks-shovels-ia" || post.id === "seniors-propietaris-economia") && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleArticleClick(post);
              }
            }}
          >
            {post.image && (
              <div className={`w-full aspect-video overflow-hidden relative ${post.id === 'recuperacio-borsa-ia' ? 'bg-[#FF0000] flex items-center justify-center' : ''}`}>
                {post.id === 'recuperacio-borsa-ia' ? (
                  <div className="w-18 h-12 bg-white/90 rounded-xl flex items-center justify-center shadow-lg">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#FF0000] ml-0.5" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                ) : (
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                )}
                {isNewPost(post.date) && (
                  <span className="absolute top-4 right-4 text-xs font-semibold text-white bg-[#9DD566] px-3 py-1.5 rounded-full shadow-lg">
                    Nou
                  </span>
                )}
              </div>
            )}
            <div className="p-6 lg:p-8 flex-1 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold text-[#9DD566] uppercase tracking-wider">
                  {post.category}
                </span>
                <span className="text-gray-300">•</span>
                <span className="text-xs text-gray-500">{post.date}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">{post.title}</h3>
              <p className="text-gray-600 mb-6 flex-1 leading-relaxed">{post.excerpt}</p>
              {(post.id === "ia-programari-creixement" || post.id === "google-alphabet-ia" || post.id === "warsh-fed" || post.id === "oracle-openai" || post.id === "panic-mercats-privats-ia" || post.id === "recuperacio-borsa-ia" || post.id === "picks-shovels-ia" || post.id === "seniors-propietaris-economia") && (
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm text-[#9DD566] font-semibold">Llegir article →</p>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default App;
